import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SidebarLayout from "./SidebarLayout";
import axios from "axios";
import "./shopStyles.css";
import "./BoothRentalManagement.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

// ── Fee helpers ──────────────────────────────────────────────────────────────
const calcStripeFee = (amount) => {
  const n = parseFloat(amount) || 0;
  return (n * 0.029 + 0.3).toFixed(2);
};
const calcNetAmount = (amount) => {
  const n = parseFloat(amount) || 0;
  return Math.max(0, n - n * 0.029 - 0.3).toFixed(2);
};

const STANDARD_BOOTH_AMENITIES = [
  "Storage",
  "Utilities",
  "WiFi",
  "Parking",
  "Reception Support",
  "Laundry Access",
  "Towels and Sanitization",
  "Retail Shelf Space",
  "Equipment Included",
  "After Hours Access",
];

const getAgreementStatusLabel = (status) => {
  const map = {
    draft: "Draft",
    issued: "Issued",
    r7_signed: "Signed by Booth Renter",
    fully_executed: "Fully Executed",
    active: "Active",
    amendment_pending: "Amendment Pending",
    suspended: "Suspended",
    pending_reinstatement: "Pending Reinstatement",
    terminated: "Terminated",
    archived: "Archived",
  };

  return map[status] || status || "-";
};

const getAgreementEventLabel = (event) => {
  const map = {
    agreement_issued: "Agreement Issued",
    agreement_viewed: "Agreement Viewed",
    agreement_signed_r7: "Signed by Booth Renter",
    agreement_signed_r1: "Countersigned by Owner",
    agreement_executed: "Agreement Activated",
    policy_applied_due_date: "Policy Due Date Applied",
    late_fee_applied: "Late Fee Applied",
    suspension_applied: "Suspension Applied",
    reinstatement_requested: "Reinstatement Requested",
    reinstatement_approved: "Reinstatement Approved",
    offer_policy_updated: "Offer Policy Updated",
    agreement_terminated: "Agreement Terminated",
  };

  return map[event] || event || "-";
};

const getAgreementActorLabel = (role) => {
  const map = {
    R1: "Owner",
    R7: "Booth Renter",
    system: "System",
    admin: "Admin",
  };

  return map[role] || role || "-";
};

const getAgreementDateLabel = (agreement) => {
  const value =
    agreement?.auditSummary?.r7SignedAt ||
    agreement?.auditSummary?.createdAt ||
    agreement?.lastEventAt ||
    null;

  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const normalizeAgreementHtml = (renderedHtml, status) => {
  if (!renderedHtml || typeof renderedHtml !== "string") {
    return "";
  }

  const isExecuted = ["fully_executed", "active"].includes(status);
  const previewTextRegex =
    /This is a preview only and does not become enforceable until signing is complete\./gi;
  const templateVersionRegex =
    /<p[^>]*>\s*Template\s+BOOTH_RENTAL_v\d+\s*\|\s*Agreement Version\s*\d+\s*<\/p>/gi;

  return renderedHtml
    .replace(/\[R1\]/gi, "")
    .replace(/\[R7\]/gi, "")
    .replace(/\(\s*R1\s*\)/gi, "")
    .replace(/\(\s*R7\s*\)/gi, "")
    .replace(/Owner\s*\(\s*R1\s*\)\s*:/gi, "Owner:")
    .replace(/Renter\s*\(\s*R7\s*\)\s*:/gi, "Booth Renter:")
    .replace(/<strong>\s*Renter\s*:/gi, "<strong>Booth Renter:")
    .replace(templateVersionRegex, "")
    .replace(
      previewTextRegex,
      isExecuted
        ? "This agreement is fully executed and enforceable."
        : "This agreement becomes enforceable once signing is complete.",
    )
    .replace(/\s+:/g, ":")
    .replace(/\s{2,}/g, " ");
};

const BoothRentalManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [businessId, setBusinessId] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocationFilter, setSelectedLocationFilter] = useState("all");

  // Offers state
  const [offers, setOffers] = useState([]);
  const [showCreateOfferModal, setShowCreateOfferModal] = useState(false);
  const [showEditOfferModal, setShowEditOfferModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [offerForm, setOfferForm] = useState({
    locationId: "",
    rentAmount: "",
    billingCycle: "monthly",
    rentCollectionMethod: "online",
    numberOfSeats: 1,
    defaultShowOnSalonPage: true,
    title: "",
    description: "",
    includedAmenities: [],
    expiresInDays: 90,
    contractTemplateId: "",
    contractPolicy: {
      dueDateRule: "cycle_start",
      gracePeriodDays: 3,
      lateFeeEnabled: false,
      lateFeeType: "fixed_amount",
      lateFeeAmountMinor: 1500,
      lateFeePercent: 5,
      lateFeeTriggerDays: 3,
      lateFeeRecurrence: "one_time",
      lateFeeRecurrenceDays: 7,
      suspensionMode: "automatic_after_days_overdue",
      suspensionTriggerDays: 7,
      reinstatementMode: "auto_after_full_payment",
      reinstatementWindowDays: 30,
    },
    acceptR1MonthlyFeeTerms: false,
    acceptedPolicyVersion: "R1-BOOTH-FEE-v1",
  });
  const [contractSettings, setContractSettings] = useState({
    rentPaymentSettings: {
      minRentAmount: 5000,
      maxRentAmount: 200000,
      allowedBillingCycles: ["weekly", "biweekly", "monthly"],
      gracePeriodDays: 3,
      allowOnlineCollection: true,
      allowOfflineTracking: true,
    },
    templates: [],
    defaultContractPolicyTemplateId: "",
  });

  // Booth renters state
  const [boothRenters, setBoothRenters] = useState([]);
  const [activeTab, setActiveTab] = useState("offers"); // 'offers' | 'renters' | 'agreements'
  const [agreements, setAgreements] = useState([]);
  const [loadingAgreements, setLoadingAgreements] = useState(false);
  const [selectedAgreementDetail, setSelectedAgreementDetail] = useState(null);
  const [selectedAgreementEvents, setSelectedAgreementEvents] = useState([]);
  const [tenantsPaymentStatus, setTenantsPaymentStatus] = useState([]);
  const [loadingPaymentStatus, setLoadingPaymentStatus] = useState(false);

  // Platform settings
  const [platformSettings, setPlatformSettings] = useState(null);

  // Rent collection state
  const [showRentModal, setShowRentModal] = useState(false);
  const [selectedRenter, setSelectedRenter] = useState(null);
  const [collectionMethod, setCollectionMethod] = useState(null); // 'online' or 'offline'
  const [offlineForm, setOfflineForm] = useState({
    paidDate: new Date().toISOString().split("T")[0],
    notes: "",
  });
  const [onlineForm, setOnlineForm] = useState({
    description: "", // e.g., "March Rent", "February Rent"
  });
  const [onlineCheckoutUrl, setOnlineCheckoutUrl] = useState(null);
  const [feeBreakdown, setFeeBreakdown] = useState(null);
  const [renterPaymentHistory, setRenterPaymentHistory] = useState({});

  // Payment links management state
  const [renterPaymentLinks, setRenterPaymentLinks] = useState({}); // Store pending links per renter

  // Email share modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({
    to: "",
    subject: "",
    message: "",
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [resendingInviteKey, setResendingInviteKey] = useState(null);

  // Renter status action modal state
  const [renterActionModal, setRenterActionModal] = useState({
    show: false,
    renter: null,
    rental: null,
    action: null, // 'suspend' | 'detach' | 'reactivate'
  });
  const [renterActionReason, setRenterActionReason] = useState("");
  const [renterActionLoading, setRenterActionLoading] = useState(false);
  const [countersignModal, setCountersignModal] = useState({
    show: false,
    agreementId: null,
    typedName: "",
    submitting: false,
  });

  // Fetch businessId (R1 owner has one business)
  const fetchBusinessData = useCallback(async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        navigate("/login");
        return;
      }

      let busId = null;

      // Try staff profile first (for R1 owner-professionals)
      try {
        const staffRes = await axios.get(`${API_BASE_URL}/api/staff/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const staffData = staffRes.data.staff || staffRes.data;

        if (staffData) {
          busId =
            typeof staffData.businessId === "object"
              ? staffData.businessId?._id
              : staffData.businessId;
        }
      } catch (error) {
      }

      // Fallback: get from shops endpoint (for pure R1 owners)
      if (!busId) {
        const shopsRes = await axios.get(`${API_BASE_URL}/api/shop/my-shops`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const shops = Array.isArray(shopsRes.data)
          ? shopsRes.data
          : shopsRes.data.shops || [];

        if (shops.length > 0) {
          busId = shops[0].businessId?._id || shops[0].businessId;
        }
      }

      if (!busId) {
        alert("No business associated with your account");
        setLoading(false);
        return;
      }

      setBusinessId(busId);
    } catch (error) {
      alert("Failed to load business data");
      setLoading(false);
    }
  }, [navigate]);

  const fetchLocations = useCallback(async () => {
    if (!businessId) return;
    try {
      const token = localStorage.getItem("ogestyle_token");
      const res = await axios.get(
        `${API_BASE_URL}/api/business/${businessId}/locations`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setLocations(res.data.locations || []);
    } catch (error) {
    }
  }, [businessId]);

  const fetchPlatformSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const res = await axios.get(
        `${API_BASE_URL}/api/platform-settings/booth-rental`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setPlatformSettings(res.data.settings);
    } catch (error) {
      setError("Failed to load platform settings. Please refresh the page.");
    }
  }, []);

  const fetchContractSettings = useCallback(async () => {
    if (!businessId) return;
    try {
      const token = localStorage.getItem("ogestyle_token");
      const res = await axios.get(
        `${API_BASE_URL}/api/business/${businessId}/booth-rental/contract-settings`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const nextSettings = res.data || {};
      setContractSettings(nextSettings);

      const defaultTemplateId =
        nextSettings.defaultContractPolicyTemplateId ||
        nextSettings.templates?.[0]?.templateId ||
        "";
      const defaultTemplate = (nextSettings.templates || []).find(
        (template) => template.templateId === defaultTemplateId,
      );

      setOfferForm((prev) => ({
        ...prev,
        contractTemplateId: defaultTemplateId,
        contractPolicy: {
          ...prev.contractPolicy,
          ...(defaultTemplate?.policy || {}),
          gracePeriodDays:
            defaultTemplate?.policy?.gracePeriodDays ??
            nextSettings.rentPaymentSettings?.gracePeriodDays ??
            prev.contractPolicy.gracePeriodDays,
        },
      }));
    } catch (error) {
    }
  }, [businessId]);

  const fetchOffers = useCallback(async () => {
    if (!businessId) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("ogestyle_token");
      const params = {};
      if (selectedLocationFilter !== "all") {
        params.locationId = selectedLocationFilter;
      }

      const res = await axios.get(
        `${API_BASE_URL}/api/business/${businessId}/booth-rental/offers`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        },
      );
      setOffers(res.data.offers || []);
    } catch (error) {
      alert("Failed to load booth rental offers");
    } finally {
      setLoading(false);
    }
  }, [businessId, selectedLocationFilter]);

  const fetchBoothRenters = useCallback(async () => {
    if (!businessId) return;

    try {
      const token = localStorage.getItem("ogestyle_token");
      const params = {};
      if (selectedLocationFilter !== "all") {
        params.locationId = selectedLocationFilter;
      }

      const res = await axios.get(
        `${API_BASE_URL}/api/booth-rental/business/${businessId}/renters`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        },
      );
      setBoothRenters(res.data.boothRenters || []);
    } catch (error) {
    }
  }, [businessId, selectedLocationFilter]);

  const fetchAgreements = useCallback(async () => {
    try {
      setLoadingAgreements(true);
      const token = localStorage.getItem("ogestyle_token");
      const params = { page: 1, limit: 50 };
      if (selectedLocationFilter !== "all") {
        params.locationId = selectedLocationFilter;
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/booth-rental/contracts/r1`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        },
      );

      setAgreements(response.data.items || []);
    } catch (error) {
      setAgreements([]);
    } finally {
      setLoadingAgreements(false);
    }
  }, [selectedLocationFilter]);

  const fetchAgreementDetail = async (agreementId) => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const [detailRes, eventsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/booth-rental/contracts/${agreementId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE_URL}/api/booth-rental/contracts/${agreementId}/events`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setSelectedAgreementDetail(detailRes.data);
      setSelectedAgreementEvents(eventsRes.data.events || []);
    } catch (error) {
      alert("Failed to load agreement details");
    }
  };

  const handleCountersignAgreement = async (agreementId) => {
    const typedName = countersignModal.typedName.trim();
    if (!typedName || typedName.length < 2) {
      alert("Please enter your legal name to countersign.");
      return;
    }

    try {
      setCountersignModal((prev) => ({ ...prev, submitting: true }));
      const token = localStorage.getItem("ogestyle_token");
      await axios.post(
        `${API_BASE_URL}/api/booth-rental/contracts/${agreementId}/sign-r1`,
        {
          typedName: typedName.trim(),
          approvalAccepted: true,
          consentTextVersion: "BOOTH_RENTAL_CONSENT_v1",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      alert("Agreement countersigned successfully.");
      fetchAgreements();
      fetchAgreementDetail(agreementId);
      setCountersignModal({
        show: false,
        agreementId: null,
        typedName: "",
        submitting: false,
      });
    } catch (error) {
      alert(error.response?.data?.message || "Failed to countersign agreement");
      setCountersignModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  const fetchTenantsPaymentStatus = useCallback(async () => {
    if (!businessId) return;

    try {
      setLoadingPaymentStatus(true);
      const token = localStorage.getItem("ogestyle_token");
      const params = {};
      if (selectedLocationFilter !== "all") {
        params.locationId = selectedLocationFilter;
      }

      const res = await axios.get(
        `${API_BASE_URL}/api/booth-rental/tenants-status`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        },
      );
      setTenantsPaymentStatus(res.data.tenants || []);
    } catch (error) {
    } finally {
      setLoadingPaymentStatus(false);
    }
  }, [businessId, selectedLocationFilter]);

  useEffect(() => {
    fetchBusinessData();
  }, [fetchBusinessData]);

  useEffect(() => {
    if (businessId) {
      fetchLocations();
      fetchOffers();
      fetchBoothRenters();
      fetchPlatformSettings();
      fetchContractSettings();
      fetchTenantsPaymentStatus();
      fetchAgreements();
    }
  }, [
    businessId,
    fetchLocations,
    fetchOffers,
    fetchBoothRenters,
    fetchPlatformSettings,
    fetchContractSettings,
    fetchTenantsPaymentStatus,
    fetchAgreements,
  ]);

  useEffect(() => {
    if (selectedLocationFilter !== "all") {
      fetchOffers();
      fetchBoothRenters();
      fetchTenantsPaymentStatus();
      fetchAgreements();
    }
  }, [
    selectedLocationFilter,
    fetchOffers,
    fetchBoothRenters,
    fetchTenantsPaymentStatus,
    fetchAgreements,
  ]);

  const handleCreateOffer = async (e) => {
    e.preventDefault();

    // Validation
    if (!offerForm.locationId) {
      alert("Please select a location");
      return;
    }

    const minRent = (contractSettings.rentPaymentSettings?.minRentAmount || 5000) / 100;
    const maxRent = (contractSettings.rentPaymentSettings?.maxRentAmount || 200000) / 100;

    if (!offerForm.rentAmount || Number(offerForm.rentAmount) < minRent) {
      alert(`Rent amount must be at least $${minRent}`);
      return;
    }

    if (Number(offerForm.rentAmount) > maxRent) {
      alert(`Rent amount must not exceed $${maxRent}`);
      return;
    }

    if (!offerForm.acceptR1MonthlyFeeTerms) {
      alert("Please accept the monthly booth renter fee terms to continue.");
      return;
    }

    try {
      const token = localStorage.getItem("ogestyle_token");
      const res = await axios.post(
        `${API_BASE_URL}/api/business/${businessId}/booth-rental/offers`,
        {
          ...offerForm,
          rentAmount: Math.round(offerForm.rentAmount * 100), // Convert to cents
          contractPolicy: {
            ...offerForm.contractPolicy,
            lateFeeAmountMinor:
              offerForm.contractPolicy?.lateFeeType === "fixed_amount"
                ? parseInt(offerForm.contractPolicy?.lateFeeAmountMinor || 0, 10)
                : null,
            lateFeePercent:
              offerForm.contractPolicy?.lateFeeType === "percentage_amount"
                ? Number(offerForm.contractPolicy?.lateFeePercent || 0)
                : null,
            lateFeeTriggerDays: parseInt(
              offerForm.contractPolicy?.lateFeeTriggerDays || 0,
              10,
            ),
            lateFeeRecurrenceDays: parseInt(
              offerForm.contractPolicy?.lateFeeRecurrenceDays || 1,
              10,
            ),
            gracePeriodDays: parseInt(
              offerForm.contractPolicy?.gracePeriodDays ||
                contractSettings.rentPaymentSettings?.gracePeriodDays ||
                3,
              10,
            ),
            suspensionTriggerDays: parseInt(
              offerForm.contractPolicy?.suspensionTriggerDays || 7,
              10,
            ),
            reinstatementWindowDays: parseInt(
              offerForm.contractPolicy?.reinstatementWindowDays || 30,
              10,
            ),
          },
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      alert("Booth rental offer created successfully!");

      // Show referral code and URL
      const { offer, signupUrl } = res.data;
      showReferralCodeModal(offer.referralCode, signupUrl);

      setShowCreateOfferModal(false);
      resetOfferForm();
      fetchOffers();
    } catch (error) {
      alert(
        error.response?.data?.error || "Failed to create booth rental offer",
      );
    }
  };

  const showReferralCodeModal = (code, url) => {
    const modal = document.createElement("div");
    modal.className = "referral-code-modal";
    modal.innerHTML = `
      <div class="modal-overlay" onclick="this.parentElement.remove()">
        <div class="modal-content" onclick="event.stopPropagation()">
          <h3>✅ Booth Rental Offer Created!</h3>
          <p>Share this referral code or URL with professionals:</p>
          <div class="referral-code-box">
            <strong>Referral Code:</strong> ${code}
            <button onclick="navigator.clipboard.writeText('${code}'); alert('Code copied!')">📋 Copy</button>
          </div>
          <div class="referral-url-box">
            <strong>Signup URL:</strong>
            <input type="text" value="${url}" readonly onclick="this.select()" />
            <button onclick="navigator.clipboard.writeText('${url}'); alert('URL copied!')">📋 Copy</button>
          </div>
          <button class="btn-primary" onclick="this.closest('.referral-code-modal').remove()">Close</button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
  };

  const resetOfferForm = () => {
    setOfferForm({
      locationId: "",
      rentAmount: "",
      billingCycle: "monthly",
      rentCollectionMethod: "online",
      numberOfSeats: 1,
      defaultShowOnSalonPage: true,
      title: "",
      description: "",
      includedAmenities: [],
      expiresInDays: 90,
      contractTemplateId: contractSettings.defaultContractPolicyTemplateId || "",
      contractPolicy: {
        dueDateRule: "cycle_start",
        gracePeriodDays: contractSettings.rentPaymentSettings?.gracePeriodDays || 3,
        lateFeeEnabled: false,
        lateFeeType: "fixed_amount",
        lateFeeAmountMinor: 1500,
        lateFeePercent: 5,
        lateFeeTriggerDays: 3,
        lateFeeRecurrence: "one_time",
        lateFeeRecurrenceDays: 7,
        suspensionMode: "automatic_after_days_overdue",
        suspensionTriggerDays: 7,
        reinstatementMode: "auto_after_full_payment",
        reinstatementWindowDays: 30,
      },
      acceptR1MonthlyFeeTerms: false,
      acceptedPolicyVersion: "R1-BOOTH-FEE-v1",
    });
  };

  const handleToggleOfferStatus = async (offerId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "paused" : "active";

    try {
      const token = localStorage.getItem("ogestyle_token");
      await axios.patch(
        `${API_BASE_URL}/api/business/${businessId}/booth-rental/offers/${offerId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      alert(
        `Offer ${newStatus === "active" ? "activated" : "paused"} successfully`,
      );
      fetchOffers();
    } catch (error) {
      alert("Failed to update offer status");
    }
  };

  const handleOpenEditModal = (offer) => {
    const defaultTemplateId =
      contractSettings.defaultContractPolicyTemplateId ||
      contractSettings.templates?.[0]?.templateId ||
      "";

    const baseContractPolicy = {
      dueDateRule: "cycle_start",
      gracePeriodDays: contractSettings.rentPaymentSettings?.gracePeriodDays || 3,
      lateFeeEnabled: false,
      lateFeeType: "fixed_amount",
      lateFeeAmountMinor: 1500,
      lateFeePercent: 5,
      lateFeeTriggerDays: 3,
      lateFeeRecurrence: "one_time",
      lateFeeRecurrenceDays: 7,
      suspensionMode: "automatic_after_days_overdue",
      suspensionTriggerDays: 7,
      reinstatementMode: "auto_after_full_payment",
      reinstatementWindowDays: 30,
    };

    setSelectedOffer(offer);
    setOfferForm({
      locationId: offer.locationId?._id || offer.locationId,
      rentAmount: (offer.rentAmount / 100).toString(),
      billingCycle: offer.billingCycle,
      rentCollectionMethod: offer.rentCollectionMethod || "online",
      numberOfSeats: offer.numberOfSeats,
      defaultShowOnSalonPage: offer.defaultShowOnSalonPage,
      title: offer.title || "",
      description: offer.description || "",
      includedAmenities: offer.includedAmenities || [],
      expiresInDays: offer.expiresInDays || 90,
      contractTemplateId: offer.contractTemplateId || defaultTemplateId,
      contractPolicy: {
        ...baseContractPolicy,
        ...(offer.contractPolicy || {}),
      },
      acceptR1MonthlyFeeTerms: offer.r1MonthlyFeeAcceptance?.accepted === true,
      acceptedPolicyVersion:
        offer.r1MonthlyFeeAcceptance?.policyVersion || "R1-BOOTH-FEE-v1",
    });
    setShowEditOfferModal(true);
  };

  const handleToggleAmenity = (amenity) => {
    const selected = offerForm.includedAmenities || [];
    const nextAmenities = selected.includes(amenity)
      ? selected.filter((item) => item !== amenity)
      : [...selected, amenity];

    setOfferForm({
      ...offerForm,
      includedAmenities: nextAmenities,
    });
  };

  const handleContractTemplateChange = (templateId) => {
    const template = (contractSettings.templates || []).find(
      (row) => row.templateId === templateId,
    );

    setOfferForm((prev) => ({
      ...prev,
      contractTemplateId: templateId,
      contractPolicy: {
        ...prev.contractPolicy,
        ...(template?.policy || {}),
      },
    }));
  };

  const updateContractPolicyField = (field, value) => {
    setOfferForm((prev) => ({
      ...prev,
      contractPolicy: {
        ...(prev.contractPolicy || {}),
        [field]: value,
      },
    }));
  };

  const handleUpdateOffer = async (e) => {
    e.preventDefault();

    if (!selectedOffer) return;

    // Validation - can now decrease seats
    if (offerForm.numberOfSeats < 1) {
      alert("Number of seats must be at least 1.");
      return;
    }

    try {
      const token = localStorage.getItem("ogestyle_token");
      await axios.patch(
        `${API_BASE_URL}/api/business/${businessId}/booth-rental/offers/${selectedOffer._id}`,
        {
          title: offerForm.title,
          description: offerForm.description,
          includedAmenities: offerForm.includedAmenities || [],
          numberOfSeats: parseInt(offerForm.numberOfSeats),
          contractTemplateId: offerForm.contractTemplateId,
          contractPolicy: {
            ...offerForm.contractPolicy,
            lateFeeAmountMinor:
              offerForm.contractPolicy?.lateFeeType === "fixed_amount"
                ? parseInt(offerForm.contractPolicy?.lateFeeAmountMinor || 0, 10)
                : null,
            lateFeePercent:
              offerForm.contractPolicy?.lateFeeType === "percentage_amount"
                ? Number(offerForm.contractPolicy?.lateFeePercent || 0)
                : null,
            lateFeeTriggerDays: parseInt(
              offerForm.contractPolicy?.lateFeeTriggerDays || 0,
              10,
            ),
            lateFeeRecurrenceDays: parseInt(
              offerForm.contractPolicy?.lateFeeRecurrenceDays || 1,
              10,
            ),
            gracePeriodDays: parseInt(
              offerForm.contractPolicy?.gracePeriodDays ||
                contractSettings.rentPaymentSettings?.gracePeriodDays ||
                3,
              10,
            ),
            suspensionTriggerDays: parseInt(
              offerForm.contractPolicy?.suspensionTriggerDays || 7,
              10,
            ),
            reinstatementWindowDays: parseInt(
              offerForm.contractPolicy?.reinstatementWindowDays || 30,
              10,
            ),
          },
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      alert("✅ Booth rental offer updated successfully!");
      setShowEditOfferModal(false);
      setSelectedOffer(null);
      resetOfferForm();
      fetchOffers();
    } catch (error) {
      alert(
        error.response?.data?.error || "Failed to update booth rental offer",
      );
    }
  };

  const handleOpenEmailModal = (offer) => {
    setSelectedOffer(offer);
    setEmailForm({
      to: "",
      subject: "",
      message: "",
    });
    setShowEmailModal(true);
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();

    if (!emailForm.to) {
      alert("Please enter an email address");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailForm.to)) {
      alert("Please enter a valid email address");
      return;
    }

    setSendingEmail(true);
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await axios.post(
        `${API_BASE_URL}/api/business/${businessId}/booth-rental/send-invite`,
        {
          email: emailForm.to,
          offerId: selectedOffer._id,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      alert(`✅ ${response.data.message}`);
      setShowEmailModal(false);
      setSelectedOffer(null);
      setEmailForm({ to: "", subject: "", message: "" });

      // Refresh offers to show updated invitation count
      fetchOffers();
    } catch (error) {
      const errorMsg =
        error.response?.data?.error || "Failed to send invitation";
      alert(`❌ ${errorMsg}`);
    } finally {
      setSendingEmail(false);
    }
  };

  const buildBoothInviteLink = (offer, email) => {
    if (!offer?.referralCode || !email) return "";
    const clientBase =
      process.env.REACT_APP_CLIENT_URL || window.location.origin;
    return `${clientBase}/booth-rental/signup/${offer.referralCode}?email=${encodeURIComponent(
      email,
    )}`;
  };

  const handleCopyInviteLink = async (offer, email, invitationStatus) => {
    if (invitationStatus && invitationStatus !== "pending") {
      alert("This invitation has already been used and the link is no longer available.");
      return;
    }

    const inviteLink = buildBoothInviteLink(offer, email);
    if (!inviteLink) {
      alert("Unable to build invite link for this invitation.");
      return;
    }

    try {
      await navigator.clipboard.writeText(inviteLink);
      alert("✅ Invitation link copied to clipboard!");
    } catch (err) {
      window.prompt("Copy this invitation link:", inviteLink);
    }
  };

  const handleResendInvite = async (offer, email, invitationStatus) => {
    if (!offer?._id || !email) return;

    if (invitationStatus && invitationStatus !== "pending") {
      alert("This invitation has already been used and cannot be resent.");
      return;
    }

    const confirmed = window.confirm(
      `Resend booth rental invitation to ${email}?`,
    );
    if (!confirmed) return;

    const requestKey = `${offer._id}:${email}`;
    setResendingInviteKey(requestKey);

    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await axios.post(
        `${API_BASE_URL}/api/business/${businessId}/booth-rental/send-invite`,
        {
          email,
          offerId: offer._id,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      alert(`✅ ${response.data.message}`);
      fetchOffers();
    } catch (error) {
      const errorMsg =
        error.response?.data?.error || "Failed to resend invitation";
      alert(`❌ ${errorMsg}`);
    } finally {
      setResendingInviteKey(null);
    }
  };

  const handleToggleRenterVisibility = async (
    staffId,
    locationId,
    currentVisibility,
  ) => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      await axios.patch(
        `${API_BASE_URL}/api/booth-rental/business/${businessId}/renters/${staffId}/visibility`,
        {
          locationId,
          showOnSalonPage: !currentVisibility,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      alert(`Visibility updated successfully`);
      fetchBoothRenters();
    } catch (error) {
      alert("Failed to update visibility");
    }
  };

  const handleRenterStatusAction = async () => {
    const { renter, rental, action } = renterActionModal;
    if (!renter || !rental || !action) return;

    setRenterActionLoading(true);
    try {
      const token = localStorage.getItem("ogestyle_token");
      const locationId = rental?.locationId?._id || rental?.locationId;

      await axios.patch(
        `${API_BASE_URL}/api/booth-rental/business/${businessId}/renters/${renter._id}/status`,
        {
          action,
          locationId,
          reason: renterActionReason.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setRenterActionModal({
        show: false,
        renter: null,
        rental: null,
        action: null,
      });
      setRenterActionReason("");
      fetchBoothRenters();
    } catch (error) {
      const msg =
        error.response?.data?.error || "Failed to update renter status";
      alert(msg);
    } finally {
      setRenterActionLoading(false);
    }
  };

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatBillingCycle = (cycle) => {
    return cycle.charAt(0).toUpperCase() + cycle.slice(1);
  };

  const selectedContractTemplate = (contractSettings.templates || []).find(
    (template) => template.templateId === offerForm.contractTemplateId,
  );

  const suspensionModeLabelMap = {
    none: "No automatic suspension",
    automatic_after_days_overdue: "Automatic suspension after overdue threshold",
    manual_by_r1: "Manual owner review",
    hybrid: "Hybrid (automatic reminders + manual suspension)",
  };

  const reinstatementModeLabelMap = {
    auto_after_full_payment: "Automatic after full payment",
    approval_required: "Owner approval required",
  };

  const contractPolicyPreview = {
    dueDateRule:
      offerForm.contractPolicy?.dueDateRule === "fixed_day_of_month"
        ? `Fixed day of month (${offerForm.contractPolicy?.fixedDayOfMonth || "-"})`
        : offerForm.contractPolicy?.dueDateRule === "offset_days_from_cycle_start"
          ? `Offset from cycle start (${offerForm.contractPolicy?.dueOffsetDays || 0} days)`
          : "Cycle start",
    gracePeriodDays: offerForm.contractPolicy?.gracePeriodDays ?? 0,
    lateFee:
      offerForm.contractPolicy?.lateFeeEnabled
        ? offerForm.contractPolicy?.lateFeeType === "percentage_amount"
          ? `${offerForm.contractPolicy?.lateFeePercent || 0}% after ${offerForm.contractPolicy?.lateFeeTriggerDays || 0} day(s)`
          : `$${((offerForm.contractPolicy?.lateFeeAmountMinor || 0) / 100).toFixed(2)} after ${offerForm.contractPolicy?.lateFeeTriggerDays || 0} day(s)`
        : "Disabled",
    suspension:
      offerForm.contractPolicy?.suspensionMode === "automatic_after_days_overdue"
        ? `${suspensionModeLabelMap[offerForm.contractPolicy?.suspensionMode] || "Automatic suspension"} (${offerForm.contractPolicy?.suspensionTriggerDays || 0} day trigger)`
        : suspensionModeLabelMap[offerForm.contractPolicy?.suspensionMode] || "No automatic suspension",
    reinstatement:
      reinstatementModeLabelMap[offerForm.contractPolicy?.reinstatementMode] ||
      "Automatic after full payment",
  };

  // Rent Collection Handlers
  const handleOpenRentCollection = (renter) => {
    const method = renter?.boothRentals?.[0]?.rentCollectionMethod || "online";
    proceedWithRentCollection(renter, method);
  };

  const proceedWithRentCollection = (renter, method) => {
    setSelectedRenter(renter);
    setCollectionMethod(method);
    setShowRentModal(true);

    if (method === "online") {
      calculateOnlineFees(renter);
    }
  };

  const calculateOnlineFees = (renter) => {
    const rental = renter.boothRentals?.[0];
    if (!rental) return;

    const rentAmount = rental.rentAmount / 100; // Convert cents to dollars
    const stripeFee = rentAmount * 0.029 + 0.3; // 2.9% + $0.30
    const netAmount = rentAmount - stripeFee;

    setFeeBreakdown({
      rentAmount: rentAmount.toFixed(2),
      stripeFee: stripeFee.toFixed(2),
      netAmount: netAmount.toFixed(2),
    });
  };

  const handleCreateOnlinePayment = async () => {
    if (!selectedRenter) return;

    const rental = selectedRenter.boothRentals?.[0];
    if (!rental) {
      alert("No rental information found");
      return;
    }

    if (!onlineForm.description || onlineForm.description.trim() === "") {
      alert("Please enter a description for this payment (e.g., 'March Rent')");
      return;
    }

    // The amount is calculated by the system from the rental record (rent plus
    // any late fee currently applicable). It is never entered by hand, so it
    // cannot drift from the agreement.
    try {
      const token = localStorage.getItem("ogestyle_token");
      const res = await axios.post(
        `${API_BASE_URL}/api/booth-rental-payments/online`,
        {
          r7StaffId: selectedRenter._id,
          description: onlineForm.description.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setOnlineCheckoutUrl(res.data.sessionUrl);
      if (res.data.breakdown) {
        setFeeBreakdown({
          rentAmount: res.data.breakdown.rentAmount,
          lateFeeAmount: res.data.breakdown.lateFeeAmount,
          total: res.data.breakdown.total,
          stripeFee: res.data.breakdown.stripeFee,
          netAmount: res.data.breakdown.r1Receives,
          periodsOwed: res.data.breakdown.periodsOwed,
          periodsLate: res.data.breakdown.periodsLate,
        });
      }

      // Store link for this renter
      setRenterPaymentLinks({
        ...renterPaymentLinks,
        [selectedRenter._id]: {
          checkoutUrl: res.data.sessionUrl,
          paymentId: res.data.paymentId,
          description: onlineForm.description.trim(),
          createdAt: new Date().toISOString(),
        },
      });

      // Don't auto-open - R1 should copy and send to R7
    } catch (error) {
      const errorMsg =
        error.response?.data?.error || "Failed to create payment link";

      // Show more informative error messages for duplicate/time restrictions
      if (errorMsg.includes("pending payment already exists")) {
        alert(
          "⚠️ A pending payment link already exists for this renter.\n\nPlease cancel the existing link first before creating a new one, or wait for the renter to pay.",
        );
      } else if (errorMsg.includes("Cannot generate new")) {
        const nextDate = error.response?.data?.nextAllowedDate;
        alert(
          `⚠️ ${errorMsg}\n\n${nextDate ? `Next payment link can be generated after: ${nextDate}` : ""}`,
        );
      } else {
        alert(errorMsg);
      }
    }
  };

  const handleMarkOfflineReceived = async () => {
    if (!selectedRenter) return;

    const rental = selectedRenter.boothRentals?.[0];
    if (!rental) {
      alert("No rental information found");
      return;
    }

    if (!offlineForm.paidDate) {
      alert("Please select the date payment was received");
      return;
    }

    try {
      const token = localStorage.getItem("ogestyle_token");
      await axios.post(
        `${API_BASE_URL}/api/booth-rental-payments/offline`,
        {
          r7StaffId: selectedRenter._id,
          amount: rental.rentAmount / 100, // Convert cents to dollars
          paidDate: offlineForm.paidDate,
          notes: offlineForm.notes,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      alert("✅ Rent payment recorded successfully!");
      setShowRentModal(false);
      resetRentModal();
      fetchBoothRenters(); // Refresh to show updated payment info
    } catch (error) {
      alert(error.response?.data?.error || "Failed to record payment");
    }
  };

  const resetRentModal = () => {
    setSelectedRenter(null);
    setCollectionMethod(null);
    setOfflineForm({
      paidDate: new Date().toISOString().split("T")[0],
      notes: "",
    });
    setOnlineForm({
      description: "",
    });
    setOnlineCheckoutUrl(null);
    setFeeBreakdown(null);
  };

  const fetchPaymentHistory = async (r7StaffId) => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const res = await axios.get(
        `${API_BASE_URL}/api/booth-rental-payments/history`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { r7StaffId },
        },
      );

      setRenterPaymentHistory((prev) => ({
        ...prev,
        [r7StaffId]: res.data.payments || [],
      }));
    } catch (error) {
    }
  };

  const togglePaymentHistory = (r7StaffId) => {
    if (renterPaymentHistory[r7StaffId]) {
      // Already loaded, just toggle visibility
      const newHistory = { ...renterPaymentHistory };
      delete newHistory[r7StaffId];
      setRenterPaymentHistory(newHistory);
    } else {
      // Load payment history
      fetchPaymentHistory(r7StaffId);
    }
  };

  // Fetch pending payment link for a renter
  const fetchPendingLink = async (r7StaffId) => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const res = await axios.get(
        `${API_BASE_URL}/api/booth-rental-payments/business/links`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Find pending link for this renter
      const pending = res.data.payments?.find(
        (p) =>
          p.status === "pending" &&
          p.r7Email ===
            boothRenters.find((r) => r._id === r7StaffId)?.userId?.email,
      );

      if (pending) {
        setRenterPaymentLinks({
          ...renterPaymentLinks,
          [r7StaffId]: {
            checkoutUrl: pending.checkoutUrl,
            paymentId: pending._id,
            description: pending.notes,
            createdAt: pending.createdAt,
            dueDate: pending.dueDate,
          },
        });
      }
    } catch (error) {
    }
  };

  // Delete payment link
  const handleCancelLink = async (r7StaffId, paymentId, r7Name) => {
    if (!window.confirm(`Delete payment link for ${r7Name}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem("ogestyle_token");
      await axios.delete(
        `${API_BASE_URL}/api/booth-rental-payments/${paymentId}/cancel`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Remove from local state
      const newLinks = { ...renterPaymentLinks };
      delete newLinks[r7StaffId];
      setRenterPaymentLinks(newLinks);

      alert(`✅ Payment link deleted for ${r7Name}`);
    } catch (error) {
      alert(error.response?.data?.error || "Failed to delete payment link");
    }
  };

  const handleSwitchCollectionMethod = async (renter, rental) => {
    const currentMethod = rental?.rentCollectionMethod || "online";
    const nextMethod = currentMethod === "online" ? "offline" : "online";
    const reason = window.prompt(
      `Optional reason for switching to ${nextMethod}:`,
      "",
    );

    if (
      !window.confirm(
        `Switch ${renter.userId?.firstName || "this renter"} to ${nextMethod} collection starting next cycle?`,
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("ogestyle_token");
      await axios.patch(
        `${API_BASE_URL}/api/booth-rental/business/${businessId}/renters/${renter._id}/collection-method`,
        {
          locationId: rental?.locationId?._id || rental?.locationId,
          newMethod: nextMethod,
          effectiveMode: "next_cycle",
          reason: reason || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      alert(`Collection method switch scheduled: ${currentMethod} -> ${nextMethod}`);
      fetchBoothRenters();
      fetchTenantsPaymentStatus();
    } catch (error) {
      alert(error.response?.data?.error || "Failed to switch collection method");
    }
  };

  return (
    <SidebarLayout>
      <div className="booth-rental-management">
        <div className="page-header">
          <h2>💼 Booth Rental Management</h2>
        </div>

        {!businessId ? (
          <div className="empty-state">
            <p>Loading your business...</p>
          </div>
        ) : (
          <>
            {/* Location Filter */}
            <div className="filters">
              <label>
                Filter by Location:
                <select
                  value={selectedLocationFilter}
                  onChange={(e) => setSelectedLocationFilter(e.target.value)}
                >
                  <option value="all">All Locations</option>
                  {locations.map((loc) => (
                    <option key={loc._id} value={loc._id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* Tabs */}
            <div className="tabs">
              <button
                className={`tab ${activeTab === "offers" ? "active" : ""}`}
                onClick={() => setActiveTab("offers")}
              >
                📋 Booth Rental Offers
              </button>
              <button
                className={`tab ${activeTab === "renters" ? "active" : ""}`}
                onClick={() => setActiveTab("renters")}
              >
                👥 Booth Renters ({boothRenters.length})
              </button>
              <button
                className={`tab ${activeTab === "agreements" ? "active" : ""}`}
                onClick={() => setActiveTab("agreements")}
              >
                🧾 Agreements ({agreements.length})
              </button>
            </div>

            {/* Offers Tab */}
            {activeTab === "offers" && (
              <div className="offers-section">
                <div className="section-header">
                  <h3>Booth Rental Offers</h3>
                  <button
                    className="btn-primary"
                    onClick={() => setShowCreateOfferModal(true)}
                  >
                    + Create New Offer
                  </button>
                </div>

                {loading ? (
                  <p>Loading offers...</p>
                ) : offers.length === 0 ? (
                  <div className="empty-state">
                    <p>📋 No booth rental offers yet</p>
                    <p>
                      Create an offer to start renting booth space to
                      professionals
                    </p>
                    <button
                      className="btn-primary"
                      onClick={() => setShowCreateOfferModal(true)}
                    >
                      Create First Offer
                    </button>
                  </div>
                ) : (
                  <div className="offers-grid">
                    {offers.map((offer) => (
                      <div
                        key={offer._id}
                        className={`offer-card status-${offer.status}`}
                      >
                        <div className="offer-header">
                          <h4>{offer.title || "Booth Rental"}</h4>
                          <span className={`status-badge ${offer.status}`}>
                            {offer.status}
                          </span>
                        </div>

                        <div className="offer-details">
                          <p>
                            <strong>Location:</strong>{" "}
                            {offer.locationId?.name || "N/A"}
                          </p>
                          <p>
                            <strong>Rent:</strong>{" "}
                            {formatCurrency(offer.rentAmount)} /{" "}
                            {formatBillingCycle(offer.billingCycle)}
                          </p>
                          <p>
                            <strong>Seats:</strong> {offer.seatsRemaining} /{" "}
                            {offer.numberOfSeats} available
                          </p>
                          <p>
                            <strong>Signups:</strong> {offer.signupsCount || 0}
                          </p>
                          {!!offer.includedAmenities?.length && (
                            <p>
                              <strong>Included:</strong> {offer.includedAmenities.join(", ")}
                            </p>
                          )}
                          {offer.description && (
                            <p className="offer-description">
                              {offer.description}
                            </p>
                          )}
                          {Array.isArray(offer.invitations) &&
                            offer.invitations.length > 0 && (
                              <div
                                style={{
                                  marginTop: "10px",
                                  padding: "10px",
                                  borderRadius: "8px",
                                  background: "#f8fafc",
                                  border: "1px solid #e2e8f0",
                                }}
                              >
                                <p
                                  style={{
                                    margin: "0 0 8px 0",
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    color: "#334155",
                                  }}
                                >
                                  Invitations
                                </p>
                                {offer.invitations
                                  .slice()
                                  .sort(
                                    (a, b) =>
                                      new Date(b.sentAt).getTime() -
                                      new Date(a.sentAt).getTime(),
                                  )
                                  .slice(0, 3)
                                  .map((invitation, idx) => {
                                    const invitationStatus =
                                      invitation.status || "pending";
                                    const isPending =
                                      invitationStatus === "pending";
                                    const key = `${offer._id}:${invitation.email}`;
                                    const isResending =
                                      resendingInviteKey === key;
                                    return (
                                      <div
                                        key={`${invitation.email}-${idx}`}
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          gap: "8px",
                                          padding: "6px 0",
                                          borderTop:
                                            idx === 0
                                              ? "none"
                                              : "1px solid #e5e7eb",
                                        }}
                                      >
                                        <div style={{ minWidth: 0 }}>
                                          <div
                                            style={{
                                              fontSize: "12px",
                                              color: "#0f172a",
                                              whiteSpace: "nowrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              maxWidth: "220px",
                                            }}
                                          >
                                            {invitation.email}
                                          </div>
                                          <div
                                            style={{
                                              fontSize: "11px",
                                              color:
                                                invitationStatus === "accepted"
                                                  ? "#166534"
                                                  : invitationStatus === "expired"
                                                    ? "#92400e"
                                                    : "#64748b",
                                            }}
                                          >
                                            {invitationStatus} •{" "}
                                            {invitation.sentAt
                                              ? new Date(
                                                  invitation.sentAt,
                                                ).toLocaleDateString()
                                              : "-"}
                                          </div>
                                        </div>
                                        {isPending ? (
                                          <div
                                            style={{
                                              display: "flex",
                                              gap: "6px",
                                              flexShrink: 0,
                                            }}
                                          >
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleCopyInviteLink(
                                                  offer,
                                                  invitation.email,
                                                  invitationStatus,
                                                )
                                              }
                                              style={{
                                                fontSize: "11px",
                                                padding: "4px 8px",
                                                borderRadius: "6px",
                                                border: "1px solid #cbd5e1",
                                                background: "#fff",
                                                cursor: "pointer",
                                              }}
                                            >
                                              Copy Link
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleResendInvite(
                                                  offer,
                                                  invitation.email,
                                                  invitationStatus,
                                                )
                                              }
                                              disabled={isResending}
                                              style={{
                                                fontSize: "11px",
                                                padding: "4px 8px",
                                                borderRadius: "6px",
                                                border: "1px solid #1d4ed8",
                                                background: "#eff6ff",
                                                color: "#1d4ed8",
                                                cursor: isResending
                                                  ? "not-allowed"
                                                  : "pointer",
                                                opacity: isResending ? 0.65 : 1,
                                              }}
                                            >
                                              {isResending ? "Sending..." : "Resend"}
                                            </button>
                                          </div>
                                        ) : (
                                          <div
                                            style={{
                                              fontSize: "11px",
                                              color: "#64748b",
                                              minWidth: "92px",
                                              textAlign: "right",
                                            }}
                                          >
                                            Used invitation
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                              </div>
                            )}
                        </div>

                        <div className="offer-actions">
                          <button
                            className="btn-primary"
                            onClick={() => handleOpenEditModal(offer)}
                            title="Edit offer details"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            className={`btn-${offer.status === "active" ? "warning" : "success"}`}
                            onClick={() =>
                              handleToggleOfferStatus(offer._id, offer.status)
                            }
                          >
                            {offer.status === "active"
                              ? "⏸ Pause"
                              : "▶ Activate"}
                          </button>
                          <button
                            className="btn-secondary"
                            onClick={() => handleOpenEmailModal(offer)}
                            title="Send invitation email"
                          >
                            📧 Send Invite
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Agreements Tab */}
            {activeTab === "agreements" && (
              <div className="renters-section">
                <h3>Booth Rental Agreements</h3>

                {loadingAgreements ? (
                  <p>Loading agreements...</p>
                ) : agreements.length === 0 ? (
                  <div className="empty-state">
                    <p>No agreements yet.</p>
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: "12px", marginBottom: "20px" }}>
                    {agreements.map((agreement) => (
                      <div
                        key={agreement.agreementId}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: "10px",
                          padding: "12px",
                          background: "#fff",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
                          <div>
                            <div style={{ fontWeight: 700 }}>{agreement.r7Name || "Renter"}</div>
                            <div style={{ fontSize: "13px", color: "#64748b" }}>
                              {agreement.locationName} • {agreement.billingCycle} • {formatCurrency(agreement.rentAmount)}
                            </div>
                            <div style={{ fontSize: "12px", color: "#64748b" }}>
                              Status: {getAgreementStatusLabel(agreement.status)}
                            </div>
                            <div style={{ fontSize: "12px", color: "#64748b" }}>
                              Agreement Date: {getAgreementDateLabel(agreement)}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button
                              className="btn-secondary"
                              onClick={() => fetchAgreementDetail(agreement.agreementId)}
                            >
                              View
                            </button>
                            {agreement.status === "r7_signed" && (
                              <button
                                className="btn-primary"
                                onClick={() =>
                                  setCountersignModal({
                                    show: true,
                                    agreementId: agreement.agreementId,
                                    typedName: "",
                                    submitting: false,
                                  })
                                }
                              >
                                Countersign
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {selectedAgreementDetail && (
                  <div
                    style={{
                      border: "1px solid #dbeafe",
                      background: "#f8fbff",
                      borderRadius: "10px",
                      padding: "14px",
                    }}
                  >
                    <h4 style={{ marginTop: 0 }}>Agreement Detail</h4>
                    <p><strong>Status:</strong> {getAgreementStatusLabel(selectedAgreementDetail.status)}</p>
                    <p><strong>Agreement Date:</strong> {getAgreementDateLabel(selectedAgreementDetail)}</p>
                    <details>
                      <summary style={{ cursor: "pointer", fontWeight: 600 }}>Rendered Contract</summary>
                      <div
                        style={{
                          marginTop: "10px",
                          background: "#fff",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          padding: "10px",
                          maxHeight: "260px",
                          overflow: "auto",
                        }}
                        dangerouslySetInnerHTML={{ __html: normalizeAgreementHtml(selectedAgreementDetail.renderedHtml, selectedAgreementDetail.status) }}
                      />
                    </details>
                    <details style={{ marginTop: "10px" }}>
                      <summary style={{ cursor: "pointer", fontWeight: 600 }}>Timeline</summary>
                      <ul style={{ marginTop: "10px" }}>
                        {selectedAgreementEvents.map((event, index) => (
                          <li key={`${event.event}-${index}`}>
                            {getAgreementEventLabel(event.event)} • {new Date(event.timestamp).toLocaleString()} • {getAgreementActorLabel(event.actorRole)}
                          </li>
                        ))}
                      </ul>
                    </details>
                  </div>
                )}
              </div>
            )}

            {/* Renters Tab */}
            {activeTab === "renters" && (
              <div className="renters-section">
                <h3>Booth Renters</h3>

                {/* Payment Status Summary */}
                {!loadingPaymentStatus && tenantsPaymentStatus.length > 0 && (
                  <div className="payment-status-summary">
                    <h4>💰 Rent Payment Status</h4>
                    <div className="status-cards-grid">
                      <div className="status-summary-card status-current-card">
                        <div className="status-card-icon">✅</div>
                        <div className="status-card-content">
                          <div className="status-card-count">
                            {
                              tenantsPaymentStatus.filter(
                                (t) => t.paymentStatus === "current",
                              ).length
                            }
                          </div>
                          <div className="status-card-label">Current</div>
                        </div>
                      </div>

                      <div className="status-summary-card status-due-soon-card">
                        <div className="status-card-icon">⚠️</div>
                        <div className="status-card-content">
                          <div className="status-card-count">
                            {
                              tenantsPaymentStatus.filter(
                                (t) => t.paymentStatus === "due_soon",
                              ).length
                            }
                          </div>
                          <div className="status-card-label">Due Soon</div>
                        </div>
                      </div>

                      <div className="status-summary-card status-overdue-card">
                        <div className="status-card-icon">🔴</div>
                        <div className="status-card-content">
                          <div className="status-card-count">
                            {
                              tenantsPaymentStatus.filter(
                                (t) => t.paymentStatus === "overdue",
                              ).length
                            }
                          </div>
                          <div className="status-card-label">Overdue</div>
                        </div>
                      </div>

                      <div className="status-summary-card status-suspended-card">
                        <div className="status-card-icon">🚫</div>
                        <div className="status-card-content">
                          <div className="status-card-count">
                            {
                              tenantsPaymentStatus.filter(
                                (t) => t.paymentStatus === "suspended",
                              ).length
                            }
                          </div>
                          <div className="status-card-label">Suspended</div>
                        </div>
                      </div>
                    </div>

                    {/* Detailed Payment Status Table */}
                    {tenantsPaymentStatus.some(
                      (t) => t.paymentStatus !== "current" && t.nextRentDueDate,
                    ) && (
                      <div className="payment-status-table-wrapper">
                        <h5>⏰ Upcoming & Overdue Payments</h5>
                        <table className="payment-status-table">
                          <thead>
                            <tr>
                              <th>Renter</th>
                              <th>Due Date</th>
                              <th>Amount</th>
                              <th>Status</th>
                              <th>Days</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tenantsPaymentStatus
                              .filter(
                                (t) =>
                                  t.paymentStatus !== "current" &&
                                  t.nextRentDueDate,
                              )
                              .sort((a, b) => {
                                // Sort by due date (earliest first)
                                return (
                                  new Date(a.nextRentDueDate) -
                                  new Date(b.nextRentDueDate)
                                );
                              })
                              .map((tenant) => {
                                const daysUntilDue = Math.ceil(
                                  (new Date(tenant.nextRentDueDate) -
                                    new Date()) /
                                    (1000 * 60 * 60 * 24),
                                );
                                const statusClass =
                                  tenant.paymentStatus === "overdue"
                                    ? "overdue"
                                    : tenant.paymentStatus === "due_soon"
                                      ? "due-soon"
                                      : "suspended";

                                return (
                                  <tr
                                    key={tenant.staffId}
                                    className={statusClass}
                                  >
                                    <td>
                                      <strong>{tenant.renterName}</strong>
                                      <br />
                                      <span
                                        style={{
                                          fontSize: "0.85em",
                                          color: "#666",
                                        }}
                                      >
                                        {tenant.locationName}
                                      </span>
                                    </td>
                                    <td>
                                      {new Date(
                                        tenant.nextRentDueDate,
                                      ).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      })}
                                    </td>
                                    <td>
                                      <strong>
                                        {formatCurrency(tenant.rentAmount)}
                                      </strong>
                                    </td>
                                    <td>
                                      <span
                                        className={`status-badge-small status-${tenant.paymentStatus.replace("_", "-")}`}
                                      >
                                        {tenant.paymentStatus === "due_soon"
                                          ? "Due Soon"
                                          : tenant.paymentStatus === "overdue"
                                            ? "Overdue"
                                            : "Suspended"}
                                      </span>
                                    </td>
                                    <td>
                                      {daysUntilDue > 0
                                        ? `${daysUntilDue} days`
                                        : daysUntilDue === 0
                                          ? "Today"
                                          : `${Math.abs(daysUntilDue)} days overdue`}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {boothRenters.length === 0 ? (
                  <div className="empty-state">
                    <p>👥 No booth renters yet</p>
                    <p>
                      Once professionals sign up using your referral links,
                      they'll appear here
                    </p>
                  </div>
                ) : (
                  <div className="renters-grid">
                    {boothRenters.map((renter) => {
                      const rental = renter.boothRentals?.[0]; // First rental for this business
                      const analytics = renter.analytics || {};

                      return (
                        <div key={renter._id} className="renter-card">
                          <div className="renter-header">
                            <img
                              src={
                                renter.userId?.profileImage ||
                                "/default-avatar.png"
                              }
                              alt={`${renter.userId?.firstName} ${renter.userId?.lastName}`}
                              className="renter-avatar"
                            />
                            <div>
                              <h4>
                                {renter.userId?.firstName}{" "}
                                {renter.userId?.lastName}
                              </h4>
                              <p className="renter-email">
                                {renter.userId?.email}
                              </p>
                            </div>
                            <span className={`status-badge ${rental?.status}`}>
                              {rental?.status}
                            </span>
                          </div>

                          <div className="renter-details">
                            <p>
                              <strong>Location:</strong>{" "}
                              {rental?.locationId?.name}
                            </p>
                            <p>
                              <strong>Rent:</strong>{" "}
                              {formatCurrency(rental?.rentAmount)} /{" "}
                              {formatBillingCycle(rental?.billingCycle)}
                            </p>
                            <p>
                              <strong>Started:</strong>{" "}
                              {new Date(rental?.startDate).toLocaleDateString()}
                            </p>
                            <p>
                              <strong>Total Paid:</strong>{" "}
                              {formatCurrency(analytics.totalPaid || 0)}
                            </p>
                            {analytics.failedPayments > 0 && (
                              <p className="warning">
                                ⚠️ <strong>Failed Payments:</strong>{" "}
                                {analytics.failedPayments}
                              </p>
                            )}
                          </div>

                          <div className="rent-collection-section">
                            <h5>💰 Rent Collection</h5>

                            {/* Check for pending payment link on mount */}
                            {rental?.rentCollectionMethod === "online" &&
                              !renterPaymentLinks[renter._id] &&
                              (() => {
                                fetchPendingLink(renter._id);
                                return null;
                              })()}

                            {/* Display pending payment link if exists */}
                            {rental?.rentCollectionMethod === "online" &&
                            renterPaymentLinks[renter._id] ? (
                              <div
                                style={{
                                  background: "#fef3c7",
                                  border: "2px solid #fbbf24",
                                  borderRadius: "8px",
                                  padding: "15px",
                                  marginBottom: "15px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "start",
                                    marginBottom: "10px",
                                  }}
                                >
                                  <div style={{ flex: 1 }}>
                                    <div
                                      style={{
                                        fontWeight: "600",
                                        color: "#92400e",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      ⏳ Pending Payment Link
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        color: "#78350f",
                                      }}
                                    >
                                      {renterPaymentLinks[renter._id]
                                        .description || "Payment Link Created"}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#a16207",
                                        marginTop: "4px",
                                      }}
                                    >
                                      Created:{" "}
                                      {new Date(
                                        renterPaymentLinks[renter._id]
                                          .createdAt,
                                      ).toLocaleDateString()}
                                    </div>
                                  </div>
                                </div>
                                <div style={{ display: "flex", gap: "8px" }}>
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(
                                        renterPaymentLinks[renter._id]
                                          .checkoutUrl,
                                      );
                                      alert(
                                        "✅ Payment link copied to clipboard!",
                                      );
                                    }}
                                    style={{
                                      flex: 1,
                                      padding: "8px 16px",
                                      background: "#3b82f6",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "6px",
                                      fontSize: "14px",
                                      fontWeight: "500",
                                      cursor: "pointer",
                                    }}
                                  >
                                    📋 Copy Link
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleCancelLink(
                                        renter._id,
                                        renterPaymentLinks[renter._id]
                                          .paymentId,
                                        `${renter.userId?.firstName} ${renter.userId?.lastName}`,
                                      )
                                    }
                                    style={{
                                      flex: 1,
                                      padding: "8px 16px",
                                      background: "#ef4444",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "6px",
                                      fontSize: "14px",
                                      fontWeight: "500",
                                      cursor: "pointer",
                                    }}
                                  >
                                    �️ Delete Link
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="rent-buttons">
                                {rental?.rentCollectionMethod === "online" ? (
                                  <button
                                    className="btn-primary"
                                    onClick={() => handleOpenRentCollection(renter)}
                                    title="Create online payment request for this renter"
                                  >
                                    💳 Create Payment Request
                                  </button>
                                ) : (
                                  <button
                                    className="btn-secondary"
                                    onClick={() => handleOpenRentCollection(renter)}
                                    title="Mark rent as received (cash/check)"
                                  >
                                    ✅ Mark as Received
                                  </button>
                                )}
                              </div>
                            )}

                            <button
                              className="btn-link"
                              onClick={() => togglePaymentHistory(renter._id)}
                            >
                              {renterPaymentHistory[renter._id]
                                ? "📕 Hide"
                                : "📖 Show"}{" "}
                              Payment History
                            </button>

                            <button
                              className="btn-link"
                              onClick={() =>
                                handleSwitchCollectionMethod(renter, rental)
                              }
                            >
                              🔁 Switch to{" "}
                              {rental?.rentCollectionMethod === "online"
                                ? "Offline"
                                : "Online"}{" "}
                              (Next Cycle)
                            </button>

                            {renterPaymentHistory[renter._id] && (
                              <div className="payment-history">
                                <table>
                                  <thead>
                                    <tr>
                                      <th>Date</th>
                                      <th>Amount</th>
                                      <th>Method</th>
                                      <th>Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {renterPaymentHistory[renter._id].length ===
                                    0 ? (
                                      <tr>
                                        <td
                                          colSpan="4"
                                          style={{ textAlign: "center" }}
                                        >
                                          No payment history yet
                                        </td>
                                      </tr>
                                    ) : (
                                      renterPaymentHistory[renter._id].map(
                                        (payment) => {
                                          const method =
                                            payment.stripeSessionId ||
                                            payment.stripeChargeId
                                              ? "online"
                                              : "offline";

                                          return (
                                            <tr key={payment._id}>
                                              <td>
                                                {payment.paidAt
                                                  ? new Date(
                                                      payment.paidAt,
                                                    ).toLocaleDateString()
                                                  : new Date(
                                                      payment.createdAt,
                                                    ).toLocaleDateString()}
                                              </td>
                                              <td>
                                                {formatCurrency(payment.amount)}
                                              </td>
                                              <td>
                                                <span
                                                  className={`method-badge ${method}`}
                                                >
                                                  {method === "online"
                                                    ? "💳 Online"
                                                    : "💰 Offline"}
                                                </span>
                                              </td>
                                              <td>
                                                <span
                                                  className={`status-badge ${payment.status}`}
                                                >
                                                  {payment.status}
                                                </span>
                                              </td>
                                            </tr>
                                          );
                                        },
                                      )
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>

                          <div className="renter-actions">
                            <label className="visibility-toggle">
                              <input
                                type="checkbox"
                                checked={rental?.showOnSalonPage}
                                onChange={() =>
                                  handleToggleRenterVisibility(
                                    renter._id,
                                    rental?.locationId?._id ||
                                      rental?.locationId,
                                    rental?.showOnSalonPage,
                                  )
                                }
                              />
                              <span>Show on Booking Page</span>
                            </label>

                            <div className="renter-status-actions">
                              {rental?.status === "active" && (
                                <button
                                  className="btn-renter-action btn-suspend"
                                  onClick={() =>
                                    setRenterActionModal({
                                      show: true,
                                      renter,
                                      rental,
                                      action: "suspend",
                                    })
                                  }
                                >
                                  ⏸ Suspend
                                </button>
                              )}
                              {(rental?.status === "suspended" ||
                                (rental?.status === "payment_overdue" &&
                                  rental?.rentPaymentStatus === "suspended")) && (
                                <button
                                  className="btn-renter-action btn-reactivate"
                                  onClick={() =>
                                    setRenterActionModal({
                                      show: true,
                                      renter,
                                      rental,
                                      action: "reactivate",
                                    })
                                  }
                                >
                                  ▶ Approve Reinstatement
                                </button>
                              )}
                              <button
                                className="btn-renter-action btn-detach"
                                onClick={() =>
                                  setRenterActionModal({
                                    show: true,
                                    renter,
                                    rental,
                                    action: "detach",
                                  })
                                }
                              >
                                🔌 Detach
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Create Offer Modal */}
            {showCreateOfferModal && (
              <div
                className="modal-overlay"
                onClick={() => setShowCreateOfferModal(false)}
              >
                <div
                  className="modal-content create-offer-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div className="co-header">
                    <div className="co-header-icon">🏪</div>
                    <div>
                      <h3>Create Booth Rental Offer</h3>
                      <p className="co-header-sub">Set up a referral offer for a professional to rent a booth at your location.</p>
                    </div>
                  </div>

                  <form onSubmit={handleCreateOffer}>

                    {/* Section: Location & Pricing */}
                    <div className="co-section">
                      <div className="co-section-label">Location &amp; Pricing</div>

                      <div className="form-row co-form-row-3">
                        <div className="form-group">
                          <label htmlFor="offer-location">Location *</label>
                          <select
                            id="offer-location"
                            value={offerForm.locationId}
                            onChange={(e) =>
                              setOfferForm({ ...offerForm, locationId: e.target.value })
                            }
                            required
                          >
                            <option value="">Select location...</option>
                            {locations.map((loc) => (
                              <option key={loc._id} value={loc._id}>
                                {loc.city && loc.state
                                  ? `${loc.city}, ${loc.state}`
                                  : loc.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label htmlFor="offer-rent">Rent Amount (USD) *</label>
                          <div className="co-input-prefix-wrap">
                            <span className="co-input-prefix">$</span>
                            <input
                              id="offer-rent"
                              type="number"
                              min={
                                Math.round(
                                  (contractSettings.rentPaymentSettings?.minRentAmount || 5000) /
                                    100,
                                )
                              }
                              max={
                                Math.round(
                                  (contractSettings.rentPaymentSettings?.maxRentAmount || 200000) /
                                    100,
                                )
                              }
                              value={offerForm.rentAmount}
                              onChange={(e) =>
                                setOfferForm({ ...offerForm, rentAmount: e.target.value })
                              }
                              placeholder="500.00"
                              required
                              className="co-prefixed-input"
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="offer-billing">Billing Cycle *</label>
                          <select
                            id="offer-billing"
                            value={offerForm.billingCycle}
                            onChange={(e) =>
                              setOfferForm({ ...offerForm, billingCycle: e.target.value })
                            }
                            required
                          >
                            {(contractSettings.rentPaymentSettings?.allowedBillingCycles || [
                              "weekly",
                              "biweekly",
                              "monthly",
                            ]).map((cycle) => (
                              <option key={cycle} value={cycle}>
                                {cycle === "biweekly"
                                  ? "Bi-Weekly"
                                  : `${cycle.charAt(0).toUpperCase()}${cycle.slice(1)}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Section: Contract Policy */}
                    <div className="co-section">
                      <div className="co-section-label">Contract Terms &amp; Policy</div>

                      <div className="form-row co-form-row-3">
                        <div className="form-group">
                          <label htmlFor="offer-contract-template">Template</label>
                          <select
                            id="offer-contract-template"
                            value={offerForm.contractTemplateId}
                            onChange={(e) => handleContractTemplateChange(e.target.value)}
                          >
                            {(contractSettings.templates || []).map((template) => (
                              <option key={template.templateId} value={template.templateId}>
                                {template.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label htmlFor="offer-grace-period">Grace Period (days)</label>
                          <input
                            id="offer-grace-period"
                            type="number"
                            min="0"
                            max="30"
                            value={offerForm.contractPolicy?.gracePeriodDays || 0}
                            onChange={(e) =>
                              updateContractPolicyField(
                                "gracePeriodDays",
                                parseInt(e.target.value || "0", 10),
                              )
                            }
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="offer-suspension-mode">Suspension Mode</label>
                          <select
                            id="offer-suspension-mode"
                            value={offerForm.contractPolicy?.suspensionMode || "automatic_after_days_overdue"}
                            onChange={(e) =>
                              updateContractPolicyField("suspensionMode", e.target.value)
                            }
                          >
                            <option value="automatic_after_days_overdue">Automatic After Overdue</option>
                            <option value="manual_by_r1">Manual Review</option>
                            <option value="none">None</option>
                            <option value="hybrid">Hybrid (Auto Reminders + Manual Suspension)</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-row co-form-row-3">
                        {(offerForm.contractPolicy?.suspensionMode === "automatic_after_days_overdue" ||
                          offerForm.contractPolicy?.suspensionMode === "hybrid") ? (
                          <div className="form-group">
                            <label htmlFor="offer-suspension-trigger">Suspend After Days Overdue</label>
                            <input
                              id="offer-suspension-trigger"
                              type="number"
                              min="1"
                              max="120"
                              value={offerForm.contractPolicy?.suspensionTriggerDays || 7}
                              onChange={(e) =>
                                updateContractPolicyField(
                                  "suspensionTriggerDays",
                                  parseInt(e.target.value || "7", 10),
                                )
                              }
                            />
                          </div>
                        ) : (
                          <div className="form-group">
                            <label htmlFor="offer-suspension-trigger-disabled">Suspend After Days Overdue</label>
                            <input
                              id="offer-suspension-trigger-disabled"
                              type="number"
                              value="-"
                              disabled
                            />
                          </div>
                        )}

                        <div className="form-group">
                          <label htmlFor="offer-reinstatement-mode">Reinstatement Mode</label>
                          <select
                            id="offer-reinstatement-mode"
                            value={offerForm.contractPolicy?.reinstatementMode || "auto_after_full_payment"}
                            onChange={(e) =>
                              updateContractPolicyField("reinstatementMode", e.target.value)
                            }
                          >
                            <option value="auto_after_full_payment">Automatic After Full Payment</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label>Enable Late Fee</label>
                          <div className="co-checkbox-field">
                            <label className="checkbox-label" style={{ marginTop: 0 }}>
                              <input
                                type="checkbox"
                                aria-label="Enable Late Fee"
                                checked={offerForm.contractPolicy?.lateFeeEnabled === true}
                                onChange={(e) =>
                                  updateContractPolicyField("lateFeeEnabled", e.target.checked)
                                }
                              />
                            </label>
                          </div>
                        </div>
                      </div>

                      {offerForm.contractPolicy?.lateFeeEnabled && (
                        <>
                          <div className="form-row co-form-row-3">
                            <div className="form-group">
                              <label htmlFor="offer-late-fee-type">Late Fee Type</label>
                              <select
                                id="offer-late-fee-type"
                                value={offerForm.contractPolicy?.lateFeeType || "fixed_amount"}
                                onChange={(e) =>
                                  updateContractPolicyField("lateFeeType", e.target.value)
                                }
                              >
                                <option value="fixed_amount">Fixed Amount</option>
                                <option value="percentage_amount">Percentage</option>
                              </select>
                            </div>

                            <div className="form-group">
                              <label htmlFor="offer-late-fee-trigger">Late Fee Trigger (days)</label>
                              <input
                                id="offer-late-fee-trigger"
                                type="number"
                                min="0"
                                max="60"
                                value={offerForm.contractPolicy?.lateFeeTriggerDays || 0}
                                onChange={(e) =>
                                  updateContractPolicyField(
                                    "lateFeeTriggerDays",
                                    parseInt(e.target.value || "0", 10),
                                  )
                                }
                              />
                            </div>

                            {offerForm.contractPolicy?.lateFeeType === "fixed_amount" ? (
                              <div className="form-group">
                                <label htmlFor="offer-late-fee-fixed">Late Fee Amount ($)</label>
                                <input
                                  id="offer-late-fee-fixed"
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={Math.round((offerForm.contractPolicy?.lateFeeAmountMinor || 0) / 100)}
                                  onChange={(e) =>
                                    updateContractPolicyField(
                                      "lateFeeAmountMinor",
                                      Math.round(Number(e.target.value || 0) * 100),
                                    )
                                  }
                                />
                              </div>
                            ) : (
                              <div className="form-group">
                                <label htmlFor="offer-late-fee-percent">Late Fee Percent (%)</label>
                                <input
                                  id="offer-late-fee-percent"
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  value={offerForm.contractPolicy?.lateFeePercent || 0}
                                  onChange={(e) =>
                                    updateContractPolicyField(
                                      "lateFeePercent",
                                      Number(e.target.value || 0),
                                    )
                                  }
                                />
                              </div>
                            )}
                          </div>
                        </>
                      )}

                      <div
                        style={{
                          marginTop: "14px",
                          padding: "14px",
                          border: "1px solid #dbeafe",
                          borderRadius: "10px",
                          background: "#f8fbff",
                        }}
                      >
                        <div style={{ fontWeight: 700, marginBottom: "8px" }}>
                          Agreement Preview
                        </div>
                        {selectedContractTemplate?.description && (
                          <div style={{ fontSize: "13px", color: "#334155", marginBottom: "8px" }}>
                            Template: {selectedContractTemplate.name} - {selectedContractTemplate.description}
                          </div>
                        )}
                        <ol style={{ margin: "0", paddingLeft: "18px", fontSize: "13px", color: "#0f172a", lineHeight: 1.65 }}>
                          <li>
                            <strong>Payment Schedule:</strong> Rent is billed on a {offerForm.billingCycle} cycle with due rule set to {contractPolicyPreview.dueDateRule}.
                          </li>
                          <li>
                            <strong>Grace Window:</strong> A grace period of {contractPolicyPreview.gracePeriodDays} day(s) applies before overdue enforcement.
                          </li>
                          <li>
                            <strong>Late Fee Terms:</strong> {contractPolicyPreview.lateFee}.
                          </li>
                          <li>
                            <strong>Suspension Terms:</strong> {contractPolicyPreview.suspension}.
                          </li>
                          <li>
                            <strong>Reinstatement:</strong> {contractPolicyPreview.reinstatement}.
                          </li>
                          <li>
                            <strong>Operational Effects:</strong> Visibility, booking access, and account state changes follow the selected suspension effects in this policy.
                          </li>
                          <li>
                            <strong>Dispute and Review:</strong> Any exceptions, waivers, or contested penalties require an owner decision and must be logged in the renter record.
                          </li>
                          <li>
                            <strong>Audit Record:</strong> This policy snapshot is embedded in the signed agreement and used as the enforcement reference for all timeline events.
                          </li>
                          {offerForm.rentCollectionMethod === "offline" ? (
                            <li>
                              <strong>Enforcement Mode:</strong> Manual owner-managed enforcement. Automated reminders, automatic late-fee accrual, automatic suspension, and automatic reinstatement are disabled and must be handled manually.
                            </li>
                          ) : (
                            <li>
                              <strong>Enforcement Mode:</strong> Automated platform enforcement. Reminders and overdue workflows can run through the platform based on this policy.
                            </li>
                          )}
                        </ol>
                      </div>
                    </div>

                    {/* Section: How will you collect rent? */}
                    <div className="co-section">
                      <div className="co-section-label">How will you collect rent?</div>

                      <div className="co-payment-cards">
                        <div
                          className={`co-payment-card ${
                            offerForm.rentCollectionMethod === "online" ? "co-payment-card--selected" : ""
                          }`}
                          onClick={() =>
                            setOfferForm({ ...offerForm, rentCollectionMethod: "online" })
                          }
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) =>
                            e.key === "Enter" &&
                            setOfferForm({ ...offerForm, rentCollectionMethod: "online" })
                          }
                        >
                          <div className="co-payment-card-icon">💳</div>
                          <div className="co-payment-card-body">
                            <strong>Online Processing</strong>
                            <span>Auto-charge the renter's card. Instant confirmation.</span>
                          </div>
                          {offerForm.rentCollectionMethod === "online" && (
                            <div className="co-payment-card-check">✓</div>
                          )}
                        </div>

                        <div
                          className={`co-payment-card ${
                            offerForm.rentCollectionMethod === "offline" ? "co-payment-card--selected" : ""
                          }`}
                          onClick={() =>
                            setOfferForm({ ...offerForm, rentCollectionMethod: "offline" })
                          }
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) =>
                            e.key === "Enter" &&
                            setOfferForm({ ...offerForm, rentCollectionMethod: "offline" })
                          }
                        >
                          <div className="co-payment-card-icon">💵</div>
                          <div className="co-payment-card-body">
                            <strong>Offline / Cash</strong>
                            <span>Collect manually and log each payment yourself.</span>
                          </div>
                          {offerForm.rentCollectionMethod === "offline" && (
                            <div className="co-payment-card-check">✓</div>
                          )}
                        </div>
                      </div>

                      {/* Live processing fee preview */}
                      {offerForm.rentCollectionMethod === "online" && offerForm.rentAmount && (
                        <div className="co-fee-preview">
                          <div className="co-fee-row">
                            <span>Renter is charged</span>
                            <strong>${parseFloat(offerForm.rentAmount || 0).toFixed(2)}</strong>
                          </div>
                          <div className="co-fee-row co-fee-row--deduction">
                            <span>Processing fee (2.9% + $0.30)</span>
                            <strong>− ${calcStripeFee(offerForm.rentAmount)}</strong>
                          </div>
                          <div className="co-fee-row co-fee-row--total">
                            <span>You receive (estimated)</span>
                            <strong>${calcNetAmount(offerForm.rentAmount)}</strong>
                          </div>
                          <p className="co-fee-note">Actual fee varies by card type and payment method. Deducted automatically per transaction.</p>
                        </div>
                      )}

                      {offerForm.rentCollectionMethod === "offline" && (
                        <div className="co-offline-expectations">
                          <h4>Offline Collection Responsibilities</h4>
                          <p>
                            You are fully responsible for collecting and enforcing
                            rent payments directly with the renter.
                          </p>
                          <ul>
                            <li>No online payment checkout links will be generated by OGESTYLE.</li>
                            <li>No automatic charge, retry, or card collection by OGESTYLE.</li>
                            <li>No payment dispute handling by OGESTYLE for offline rent.</li>
                            <li>No automatic rent receipts or payout routing from OGESTYLE.</li>
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Section: Capacity & Details */}
                    <div className="co-section">
                      <div className="co-section-label">Capacity &amp; Details</div>

                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="offer-seats">Number of Seats *</label>
                          <input
                            id="offer-seats"
                            type="number"
                            min="1"
                            max={
                              platformSettings?.boothRenterSettings?.offerDefaults
                                ?.maxSeatsPerOffer || 10
                            }
                            value={offerForm.numberOfSeats}
                            onChange={(e) =>
                              setOfferForm({
                                ...offerForm,
                                numberOfSeats:
                                  parseInt(e.target.value, 10) || 1,
                              })
                            }
                            required
                          />
                          <small>How many professionals can sign up via this offer?</small>
                        </div>

                        <div className="form-group">
                          <label htmlFor="offer-expires">Offer Expires In</label>
                          <select
                            id="offer-expires"
                            value={offerForm.expiresInDays}
                            onChange={(e) =>
                              setOfferForm({
                                ...offerForm,
                                expiresInDays:
                                  parseInt(e.target.value, 10) || 30,
                              })
                            }
                          >
                            <option value={30}>30 days</option>
                            <option value={60}>60 days</option>
                            <option value={90}>90 days</option>
                            <option value={180}>180 days</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="offer-title">Offer Title <span className="co-optional">(optional)</span></label>
                        <input
                          id="offer-title"
                          type="text"
                          value={offerForm.title}
                          onChange={(e) =>
                            setOfferForm({ ...offerForm, title: e.target.value })
                          }
                          placeholder="e.g., Chair Rental – Downtown Studio"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="offer-description">Description <span className="co-optional">(optional)</span></label>
                        <textarea
                          id="offer-description"
                          value={offerForm.description}
                          onChange={(e) =>
                            setOfferForm({ ...offerForm, description: e.target.value })
                          }
                          placeholder="What's included? (e.g., storage, equipment, utilities, Wi-Fi)"
                          rows="3"
                        />
                      </div>

                      <div className="form-group">
                        <label>What's Included <span className="co-optional">(optional)</span></label>
                        <div className="co-amenities-grid">
                          {STANDARD_BOOTH_AMENITIES.map((amenity) => (
                            <label key={amenity} className="co-amenity-item">
                              <input
                                type="checkbox"
                                checked={(offerForm.includedAmenities || []).includes(amenity)}
                                onChange={() => handleToggleAmenity(amenity)}
                              />
                              <span>{amenity}</span>
                            </label>
                          ))}
                        </div>
                        <small>
                          Select standard items included in this booth rental.
                          Use Description for any custom terms.
                        </small>
                      </div>

                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={offerForm.defaultShowOnSalonPage}
                          onChange={(e) =>
                            setOfferForm({
                              ...offerForm,
                              defaultShowOnSalonPage: e.target.checked,
                            })
                          }
                        />
                        <span>Show new renters on the booking page by default</span>
                      </label>

                      <label className="checkbox-label" style={{ marginTop: "10px" }}>
                        <input
                          type="checkbox"
                          checked={offerForm.acceptR1MonthlyFeeTerms}
                          onChange={(e) =>
                            setOfferForm({
                              ...offerForm,
                              acceptR1MonthlyFeeTerms: e.target.checked,
                            })
                          }
                          required
                        />
                        <span>
                          I acknowledge and accept the monthly booth renter fee for
                          each active booth renter tied to this offer.
                        </span>
                      </label>
                    </div>

                    {/* Live offer summary */}
                    {offerForm.rentAmount && offerForm.billingCycle && (
                      <div className="co-offer-summary">
                        <span className="co-offer-summary-pill">
                          ${parseFloat(offerForm.rentAmount || 0).toFixed(2)} /{" "}
                          {offerForm.billingCycle}
                        </span>
                        <span className="co-offer-summary-pill">
                          {offerForm.numberOfSeats} seat{offerForm.numberOfSeats !== 1 ? "s" : ""}
                        </span>
                        <span className="co-offer-summary-pill">
                          {offerForm.rentCollectionMethod === "online" ? "💳 Online" : "💵 Cash"}
                        </span>
                      </div>
                    )}

                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => {
                          setShowCreateOfferModal(false);
                          resetOfferForm();
                        }}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn-primary">
                        Create Offer
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Edit Offer Modal */}
            {showEditOfferModal && selectedOffer && (
              <div
                className="modal-overlay"
                onClick={() => {
                  setShowEditOfferModal(false);
                  setSelectedOffer(null);
                  resetOfferForm();
                }}
              >
                <div
                  className="modal-content create-offer-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="co-header">
                    <div className="co-header-icon">✏️</div>
                    <div>
                      <h3>Edit Booth Rental Offer</h3>
                      <p className="co-header-sub">Review full offer and contract context. Editable fields remain seats, title, description, and included amenities.</p>
                    </div>
                  </div>

                  <div className="edit-offer-info">
                    <strong>Note:</strong> Contract policy updates apply to future agreement generation from this offer. Existing signed agreements remain immutable.
                  </div>

                  <form onSubmit={handleUpdateOffer}>
                    <div className="co-section">
                      <div className="co-section-label">Creation Snapshot</div>

                      <div className="form-row co-form-row-3">
                        <div className="form-group">
                          <label>Offer Created</label>
                          <input
                            type="text"
                            value={
                              selectedOffer.createdAt
                                ? new Date(selectedOffer.createdAt).toLocaleString()
                                : "-"
                            }
                            disabled
                            className="input-disabled"
                          />
                        </div>

                        <div className="form-group">
                          <label>Referral Code</label>
                          <input
                            type="text"
                            value={selectedOffer.referralCode || "-"}
                            disabled
                            className="input-disabled"
                          />
                        </div>

                        <div className="form-group">
                          <label>Offer Expires</label>
                          <input
                            type="text"
                            value={
                              selectedOffer.expiresAt
                                ? new Date(selectedOffer.expiresAt).toLocaleDateString()
                                : "No expiration"
                            }
                            disabled
                            className="input-disabled"
                          />
                        </div>
                      </div>

                      <div className="form-row co-form-row-3">
                        <div className="form-group">
                          <label>Location</label>
                          <input
                            type="text"
                            value={selectedOffer.locationId?.name || "N/A"}
                            disabled
                            className="input-disabled"
                          />
                        </div>

                        <div className="form-group">
                          <label>Rent Amount</label>
                          <input
                            type="text"
                            value={`$${offerForm.rentAmount}`}
                            disabled
                            className="input-disabled"
                          />
                        </div>

                        <div className="form-group">
                          <label>Billing Cycle</label>
                          <input
                            type="text"
                            value={
                              offerForm.billingCycle.charAt(0).toUpperCase() +
                              offerForm.billingCycle.slice(1)
                            }
                            disabled
                            className="input-disabled"
                          />
                        </div>
                      </div>

                      <div className="form-row co-form-row-3">
                        <div className="form-group">
                          <label>Collection Method</label>
                          <input
                            type="text"
                            value={
                              offerForm.rentCollectionMethod === "offline"
                                ? "Offline (Cash/Manual)"
                                : "Online (Card Processing)"
                            }
                            disabled
                            className="input-disabled"
                          />
                        </div>

                        <div className="form-group">
                          <label>Template</label>
                          <input
                            type="text"
                            value={
                              selectedOffer.contractTemplateName ||
                              selectedContractTemplate?.name ||
                              "Default"
                            }
                            disabled
                            className="input-disabled"
                          />
                        </div>

                        <div className="form-group">
                          <label>Fee Policy Version</label>
                          <input
                            type="text"
                            value={offerForm.acceptedPolicyVersion || "R1-BOOTH-FEE-v1"}
                            disabled
                            className="input-disabled"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="co-section">
                      <div className="co-section-label">Contract Terms &amp; Policy</div>

                      <div className="form-row co-form-row-3">
                        <div className="form-group">
                          <label htmlFor="edit-offer-contract-template">Template</label>
                          <select
                            id="edit-offer-contract-template"
                            value={offerForm.contractTemplateId}
                            onChange={(e) => handleContractTemplateChange(e.target.value)}
                          >
                            {(contractSettings.templates || []).map((template) => (
                              <option key={template.templateId} value={template.templateId}>
                                {template.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label htmlFor="edit-offer-grace-period">Grace Period (days)</label>
                          <input
                            id="edit-offer-grace-period"
                            type="number"
                            min="0"
                            max="30"
                            value={offerForm.contractPolicy?.gracePeriodDays || 0}
                            onChange={(e) =>
                              updateContractPolicyField(
                                "gracePeriodDays",
                                parseInt(e.target.value || "0", 10),
                              )
                            }
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="edit-offer-suspension-mode">Suspension Mode</label>
                          <select
                            id="edit-offer-suspension-mode"
                            value={offerForm.contractPolicy?.suspensionMode || "automatic_after_days_overdue"}
                            onChange={(e) =>
                              updateContractPolicyField("suspensionMode", e.target.value)
                            }
                          >
                            <option value="automatic_after_days_overdue">Automatic After Overdue</option>
                            <option value="manual_by_r1">Manual Review</option>
                            <option value="none">None</option>
                            <option value="hybrid">Hybrid (Auto Reminders + Manual Suspension)</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-row co-form-row-3">
                        {(offerForm.contractPolicy?.suspensionMode === "automatic_after_days_overdue" ||
                          offerForm.contractPolicy?.suspensionMode === "hybrid") ? (
                          <div className="form-group">
                            <label htmlFor="edit-offer-suspension-trigger">Suspend After Days Overdue</label>
                            <input
                              id="edit-offer-suspension-trigger"
                              type="number"
                              min="1"
                              max="120"
                              value={offerForm.contractPolicy?.suspensionTriggerDays || 7}
                              onChange={(e) =>
                                updateContractPolicyField(
                                  "suspensionTriggerDays",
                                  parseInt(e.target.value || "7", 10),
                                )
                              }
                            />
                          </div>
                        ) : (
                          <div className="form-group">
                            <label htmlFor="edit-offer-suspension-trigger-disabled">Suspend After Days Overdue</label>
                            <input
                              id="edit-offer-suspension-trigger-disabled"
                              type="number"
                              value="-"
                              disabled
                            />
                          </div>
                        )}

                        <div className="form-group">
                          <label htmlFor="edit-offer-reinstatement-mode">Reinstatement Mode</label>
                          <select
                            id="edit-offer-reinstatement-mode"
                            value={offerForm.contractPolicy?.reinstatementMode || "auto_after_full_payment"}
                            onChange={(e) =>
                              updateContractPolicyField("reinstatementMode", e.target.value)
                            }
                          >
                            <option value="auto_after_full_payment">Automatic After Full Payment</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label>Enable Late Fee</label>
                          <div className="co-checkbox-field">
                            <label className="checkbox-label" style={{ marginTop: 0 }}>
                              <input
                                type="checkbox"
                                aria-label="Enable Late Fee"
                                checked={offerForm.contractPolicy?.lateFeeEnabled === true}
                                onChange={(e) =>
                                  updateContractPolicyField("lateFeeEnabled", e.target.checked)
                                }
                              />
                            </label>
                          </div>
                        </div>
                      </div>

                      {offerForm.contractPolicy?.lateFeeEnabled && (
                        <div className="form-row co-form-row-3">
                          <div className="form-group">
                            <label htmlFor="edit-offer-late-fee-type">Late Fee Type</label>
                            <select
                              id="edit-offer-late-fee-type"
                              value={offerForm.contractPolicy?.lateFeeType || "fixed_amount"}
                              onChange={(e) =>
                                updateContractPolicyField("lateFeeType", e.target.value)
                              }
                            >
                              <option value="fixed_amount">Fixed Amount</option>
                              <option value="percentage_amount">Percentage</option>
                            </select>
                          </div>

                          <div className="form-group">
                            <label htmlFor="edit-offer-late-fee-trigger">Late Fee Trigger (days)</label>
                            <input
                              id="edit-offer-late-fee-trigger"
                              type="number"
                              min="0"
                              max="60"
                              value={offerForm.contractPolicy?.lateFeeTriggerDays || 0}
                              onChange={(e) =>
                                updateContractPolicyField(
                                  "lateFeeTriggerDays",
                                  parseInt(e.target.value || "0", 10),
                                )
                              }
                            />
                          </div>

                          {offerForm.contractPolicy?.lateFeeType === "fixed_amount" ? (
                            <div className="form-group">
                              <label htmlFor="edit-offer-late-fee-fixed">Late Fee Amount ($)</label>
                              <input
                                id="edit-offer-late-fee-fixed"
                                type="number"
                                min="0"
                                step="1"
                                value={Math.round((offerForm.contractPolicy?.lateFeeAmountMinor || 0) / 100)}
                                onChange={(e) =>
                                  updateContractPolicyField(
                                    "lateFeeAmountMinor",
                                    Math.round(Number(e.target.value || 0) * 100),
                                  )
                                }
                              />
                            </div>
                          ) : (
                            <div className="form-group">
                              <label htmlFor="edit-offer-late-fee-percent">Late Fee Percent (%)</label>
                              <input
                                id="edit-offer-late-fee-percent"
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={offerForm.contractPolicy?.lateFeePercent || 0}
                                onChange={(e) =>
                                  updateContractPolicyField(
                                    "lateFeePercent",
                                    Number(e.target.value || 0),
                                  )
                                }
                              />
                            </div>
                          )}
                        </div>
                      )}

                      <div
                        style={{
                          marginTop: "14px",
                          padding: "14px",
                          border: "1px solid #dbeafe",
                          borderRadius: "10px",
                          background: "#f8fbff",
                        }}
                      >
                        <div style={{ fontWeight: 700, marginBottom: "8px" }}>
                          Agreement Policy Snapshot
                        </div>
                        <ol style={{ margin: "0", paddingLeft: "18px", fontSize: "13px", color: "#0f172a", lineHeight: 1.65 }}>
                          <li>
                            <strong>Payment Schedule:</strong> Rent is billed on a {offerForm.billingCycle} cycle with due rule set to {contractPolicyPreview.dueDateRule}.
                          </li>
                          <li>
                            <strong>Grace Window:</strong> A grace period of {contractPolicyPreview.gracePeriodDays} day(s) applies before overdue enforcement.
                          </li>
                          <li>
                            <strong>Late Fee Terms:</strong> {contractPolicyPreview.lateFee}.
                          </li>
                          <li>
                            <strong>Suspension Terms:</strong> {contractPolicyPreview.suspension}.
                          </li>
                          <li>
                            <strong>Reinstatement:</strong> {contractPolicyPreview.reinstatement}.
                          </li>
                          <li>
                            <strong>Audit Record:</strong> This policy snapshot remains attached to agreements generated from this offer.
                          </li>
                        </ol>
                      </div>
                    </div>

                    <div className="co-section">
                      <div className="co-section-label">Editable Offer Details</div>

                      <div className="form-group">
                        <label>
                          Number of Seats *
                        <input
                          type="number"
                          min="1"
                          max={
                            platformSettings?.boothRenterSettings?.offerDefaults
                              ?.maxSeatsPerOffer || 10
                          }
                          value={offerForm.numberOfSeats}
                          onChange={(e) =>
                            setOfferForm({
                              ...offerForm,
                              numberOfSeats:
                                parseInt(e.target.value, 10) || 1,
                            })
                          }
                          required
                        />
                      </label>
                      <small>
                        Current: {selectedOffer.numberOfSeats} seats (
                        {selectedOffer.seatsRemaining} remaining). You can
                        increase or decrease as needed.
                      </small>
                      </div>

                      <div className="form-group">
                        <label>
                          Title
                        <input
                          type="text"
                          value={offerForm.title}
                          onChange={(e) =>
                            setOfferForm({
                              ...offerForm,
                              title: e.target.value,
                            })
                          }
                          placeholder="e.g., Booth Rental - Downtown Location"
                        />
                      </label>
                      </div>

                      <div className="form-group">
                        <label>
                          Description
                        <textarea
                          value={offerForm.description}
                          onChange={(e) =>
                            setOfferForm({
                              ...offerForm,
                              description: e.target.value,
                            })
                          }
                          placeholder="Describe what's included (e.g., storage, equipment, utilities)"
                          rows="3"
                        />
                      </label>
                      </div>

                      <div className="form-group">
                        <label>What's Included</label>
                        <div className="co-amenities-grid">
                          {STANDARD_BOOTH_AMENITIES.map((amenity) => (
                            <label key={amenity} className="co-amenity-item">
                              <input
                                type="checkbox"
                                checked={(offerForm.includedAmenities || []).includes(amenity)}
                                onChange={() => handleToggleAmenity(amenity)}
                              />
                              <span>{amenity}</span>
                            </label>
                          ))}
                        </div>
                        <small>
                          Keep this in sync with your offer details for future ads.
                        </small>
                      </div>
                    </div>

                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => {
                          setShowEditOfferModal(false);
                          setSelectedOffer(null);
                          resetOfferForm();
                        }}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn-primary">
                        Update Offer
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Rent Collection Modal */}
            {showRentModal && selectedRenter && (
              <div
                className="modal-overlay"
                onClick={() => {
                  setShowRentModal(false);
                  resetRentModal();
                }}
              >
                <div
                  className="modal-content rent-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="rent-modal-header">
                    <div className="rent-modal-avatar">
                      {selectedRenter.userId?.firstName?.[0]}
                      {selectedRenter.userId?.lastName?.[0]}
                    </div>
                    <div className="rent-modal-title-group">
                      <h3>Collect Rent</h3>
                      <span className="rent-modal-renter-name">
                        {selectedRenter.userId?.firstName}{" "}
                        {selectedRenter.userId?.lastName}
                      </span>
                    </div>
                    <span className={`rent-method-badge rent-method-badge--${collectionMethod}`}>
                      {collectionMethod === "online" ? "💳 Online" : "✅ Offline"}
                    </span>
                  </div>

                  <div className="rent-info-stats">
                    <div className="rent-stat">
                      <span className="rent-stat-label">Rent Amount</span>
                      <span className="rent-stat-value">
                        {formatCurrency(
                          selectedRenter.boothRentals?.[0]?.rentAmount,
                        )}
                      </span>
                    </div>
                    <div className="rent-stat-divider" />
                    <div className="rent-stat">
                      <span className="rent-stat-label">Billing Cycle</span>
                      <span className="rent-stat-value">
                        {formatBillingCycle(
                          selectedRenter.boothRentals?.[0]?.billingCycle,
                        )}
                      </span>
                    </div>
                  </div>

                  {collectionMethod === "online" && (
                    <div className="online-collection">
                      <p className="info-text">
                        A secure payment link will be sent to{" "}
                        {selectedRenter.userId?.firstName}. They pay online
                        and you receive the net amount automatically after
                        processing fees.
                      </p>

                      {feeBreakdown && (
                        <div className="fee-card">
                          <div className="fee-card-row">
                            <span className="fee-card-label">
                              {Number(feeBreakdown.periodsOwed || 1) > 1
                                ? `Rent (${feeBreakdown.periodsOwed} periods)`
                                : "Rent Amount"}
                            </span>
                            <span className="fee-card-amount">${feeBreakdown.rentAmount}</span>
                          </div>
                          {Number(feeBreakdown.lateFeeAmount || 0) > 0 && (
                            <div className="fee-card-row">
                              <span className="fee-card-label">
                                {Number(feeBreakdown.periodsLate || 1) > 1
                                  ? `Late Fee (${feeBreakdown.periodsLate} periods)`
                                  : "Late Fee"}
                              </span>
                              <span className="fee-card-amount">
                                +${feeBreakdown.lateFeeAmount}
                              </span>
                            </div>
                          )}
                          <div className="fee-card-row">
                            <span className="fee-card-label">Total Charged</span>
                            <span className="fee-card-amount">
                              ${feeBreakdown.total || feeBreakdown.rentAmount}
                            </span>
                          </div>
                          <div className="fee-card-row fee-card-row--deduction">
                            <span className="fee-card-label">Processing Fee</span>
                            <span className="fee-card-amount fee-card-amount--negative">
                              −${parseFloat(feeBreakdown.stripeFee).toFixed(2)}
                            </span>
                          </div>
                          <div className="fee-card-divider" />
                          <div className="fee-card-row fee-card-row--total">
                            <span className="fee-card-label">You'll Receive</span>
                            <span className="fee-card-amount fee-card-amount--positive">${feeBreakdown.netAmount}</span>
                          </div>
                        </div>
                      )}

                      <div className="form-group form-group-spaced">
                        <label>
                          <strong>Amount</strong>
                          <input
                            type="text"
                            value={`$${(selectedRenter?.boothRentals?.[0]?.rentAmount / 100 || 0).toFixed(2)}${selectedRenter?.boothRentals?.[0]?.billingCycle ? ` / ${selectedRenter.boothRentals[0].billingCycle}` : ""}`}
                            readOnly
                            disabled
                            className="form-input-full"
                          />
                        </label>
                        <small className="form-helper-text">
                          💡 Calculated by OGESTYLE from this renter's agreed
                          rent. Any late fee that applies is added
                          automatically — the amount cannot be edited.
                        </small>
                      </div>

                      <div className="form-group form-group-spaced">
                        <label>
                          <strong>Payment Description *</strong>
                          <input
                            type="text"
                            value={onlineForm.description}
                            onChange={(e) =>
                              setOnlineForm({
                                ...onlineForm,
                                description: e.target.value,
                              })
                            }
                            placeholder="e.g., March 2026 Rent, Weekly Rent 3/15-3/22"
                            maxLength="50"
                            disabled={!!onlineCheckoutUrl}
                            className="form-input-full"
                          />
                        </label>
                        <small className="form-helper-text">
                          This helps you identify the payment (max 50
                          characters)
                        </small>
                      </div>

                      {!onlineCheckoutUrl ? (
                        <button
                          className="btn-primary"
                          onClick={handleCreateOnlinePayment}
                        >
                          💳 Create Payment Invoice
                        </button>
                      ) : (
                        <div className="checkout-success">
                          <p className="success-message">
                            ✅ Payment invoice created!
                          </p>
                          <p>
                            {selectedRenter.userId?.firstName} will receive:
                          </p>
                          <ul>
                            <li>📧 Email notification with payment link</li>
                            <li>🔔 Dashboard notification to pay rent</li>
                          </ul>
                          <p className="success-note">
                            They can pay directly from their dashboard.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {collectionMethod === "offline" && (
                    <div className="offline-collection">
                      <p className="info-text">
                        Record rent received via cash, check, or another offline
                        method. No platform processing fees.
                      </p>

                      <div className="offline-form">
                        <div className="form-group">
                          <label>
                            Date Received *
                            <input
                              type="date"
                              value={offlineForm.paidDate}
                              onChange={(e) =>
                                setOfflineForm({
                                  ...offlineForm,
                                  paidDate: e.target.value,
                                })
                              }
                              required
                            />
                          </label>
                        </div>

                        <div className="form-group">
                          <label>
                            Notes (Optional)
                            <textarea
                              value={offlineForm.notes}
                              onChange={(e) =>
                                setOfflineForm({
                                  ...offlineForm,
                                  notes: e.target.value,
                                })
                              }
                              placeholder="e.g., Received cash, Check #1234, etc."
                              rows="3"
                            />
                          </label>
                        </div>

                        <button
                          className="btn-primary"
                          onClick={handleMarkOfflineReceived}
                        >
                          ✅ Mark as Received -{" "}
                          {formatCurrency(
                            selectedRenter.boothRentals?.[0]?.rentAmount,
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  <button
                    className="rent-modal-cancel"
                    onClick={() => {
                      setShowRentModal(false);
                      resetRentModal();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {countersignModal.show && (
              <div
                className="modal-overlay"
                onClick={() => {
                  if (!countersignModal.submitting) {
                    setCountersignModal({
                      show: false,
                      agreementId: null,
                      typedName: "",
                      submitting: false,
                    });
                  }
                }}
              >
                <div
                  className="modal-content"
                  onClick={(e) => e.stopPropagation()}
                  style={{ maxWidth: "520px" }}
                >
                  <h3 style={{ marginTop: 0, marginBottom: "8px" }}>Countersign Agreement</h3>
                  <p style={{ marginTop: 0, color: "#64748b", fontSize: "14px" }}>
                    Finalize this agreement as the owner with your legal typed signature.
                  </p>

                  <div
                    style={{
                      border: "1px solid #dbeafe",
                      background: "#f8fbff",
                      borderRadius: "10px",
                      padding: "12px",
                      marginBottom: "14px",
                      fontSize: "13px",
                    }}
                  >
                    Agreement ID: <strong>{countersignModal.agreementId}</strong>
                  </div>

                  <div className="form-group" style={{ marginBottom: "0" }}>
                    <label htmlFor="countersign-typed-name">Typed Signature (Legal Name)</label>
                    <input
                      id="countersign-typed-name"
                      type="text"
                      value={countersignModal.typedName}
                      onChange={(e) =>
                        setCountersignModal((prev) => ({
                          ...prev,
                          typedName: e.target.value,
                        }))
                      }
                      placeholder="Type your legal name"
                      disabled={countersignModal.submitting}
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() =>
                        setCountersignModal({
                          show: false,
                          agreementId: null,
                          typedName: "",
                          submitting: false,
                        })
                      }
                      disabled={countersignModal.submitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => handleCountersignAgreement(countersignModal.agreementId)}
                      disabled={countersignModal.submitting}
                    >
                      {countersignModal.submitting
                        ? "Countersigning..."
                        : "Confirm Countersign"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Renter Status Action Confirmation Modal */}
            {renterActionModal.show && renterActionModal.renter && (
              <div
                className="modal-overlay"
                onClick={() => {
                  if (!renterActionLoading) {
                    setRenterActionModal({
                      show: false,
                      renter: null,
                      rental: null,
                      action: null,
                    });
                    setRenterActionReason("");
                  }
                }}
              >
                <div
                  className="modal-content"
                  onClick={(e) => e.stopPropagation()}
                  style={{ maxWidth: "480px" }}
                >
                  <h3 style={{ marginTop: 0 }}>
                    {renterActionModal.action === "suspend" &&
                      "⏸ Suspend Booth Renter"}
                    {renterActionModal.action === "detach" &&
                      "🔌 Detach Booth Renter"}
                    {renterActionModal.action === "reactivate" &&
                      "▶ Approve Reinstatement"}
                  </h3>

                  <p
                    style={{
                      color: "#374151",
                      fontSize: "14px",
                      lineHeight: "1.6",
                    }}
                  >
                    {renterActionModal.action === "suspend" && (
                      <>
                        You are about to <strong>suspend</strong>{" "}
                        <strong>
                          {renterActionModal.renter.firstName}{" "}
                          {renterActionModal.renter.lastName}
                        </strong>
                        . Their profile will be hidden from your booking page.
                        <br />
                        <br />
                        <strong style={{ color: "#dc2626" }}>
                          No refund will be issued for the current billing
                          period.
                        </strong>
                      </>
                    )}
                    {renterActionModal.action === "detach" && (
                      <>
                        You are about to <strong>detach</strong>{" "}
                        this renter from your business location:{" "}
                        <strong>
                          {renterActionModal.renter.firstName}{" "}
                          {renterActionModal.renter.lastName}
                        </strong>
                        . This does <strong>not</strong> delete their profile from OGESTYLE.
                        They can still be attached to another owner/location later.
                        <br />
                        <br />
                        <strong style={{ color: "#dc2626" }}>
                          Existing financial obligations and records remain in
                          the platform audit history.
                        </strong>
                      </>
                    )}
                    {renterActionModal.action === "reactivate" && (
                      <>
                        Approve reinstatement for{" "}
                        <strong>
                          {renterActionModal.renter.firstName}{" "}
                          {renterActionModal.renter.lastName}
                        </strong>
                        . This will restore active status and visibility on your booking page.
                      </>
                    )}
                  </p>

                  <div className="form-group" style={{ marginTop: "16px" }}>
                    <label style={{ fontSize: "13px", color: "#6b7280" }}>
                      Reason (optional — will be included in the notification
                      email):
                    </label>
                    <textarea
                      rows={3}
                      maxLength={500}
                      value={renterActionReason}
                      onChange={(e) => setRenterActionReason(e.target.value)}
                      placeholder="e.g. Non-payment of agreed rent"
                      style={{
                        width: "100%",
                        marginTop: "6px",
                        padding: "8px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "14px",
                        resize: "vertical",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      justifyContent: "flex-end",
                      marginTop: "24px",
                    }}
                  >
                    <button
                      className="btn-secondary"
                      onClick={() => {
                        setRenterActionModal({
                          show: false,
                          renter: null,
                          rental: null,
                          action: null,
                        });
                        setRenterActionReason("");
                      }}
                      disabled={renterActionLoading}
                    >
                      Cancel
                    </button>
                    <button
                      className={
                        renterActionModal.action === "reactivate"
                          ? "btn-primary"
                          : renterActionModal.action === "suspend"
                            ? "btn-warning"
                            : "btn-danger"
                      }
                      onClick={handleRenterStatusAction}
                      disabled={renterActionLoading}
                    >
                      {renterActionLoading
                        ? "Processing..."
                        : renterActionModal.action === "suspend"
                          ? "Confirm Suspension"
                          : renterActionModal.action === "detach"
                            ? "Confirm Detach"
                            : "Confirm Reinstatement"}
                    </button>
                  </div>
                </div>
              </div>
            )}

          </>
        )}
      </div>

      {/* Email Share Modal */}
      {showEmailModal && selectedOffer && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowEmailModal(false);
            setSelectedOffer(null);
            setEmailForm({ to: "", subject: "", message: "" });
          }}
        >
          <div
            className="modal-content email-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="email-modal-header">
              <h3>
                <span>📧</span>
                <span>Send Booth Rental Invitation</span>
              </h3>
            </div>

            {/* Offer Info Badge */}
            <div className="email-offer-badge">
              <div className="email-offer-card">
                <span className="email-offer-icon">💼</span>
                <div className="email-offer-details">
                  <div className="email-offer-title">
                    {selectedOffer.title || "Booth Rental"}
                  </div>
                  <div className="email-offer-price">
                    ${(selectedOffer.rentAmount / 100).toFixed(2)} /{" "}
                    {selectedOffer.billingCycle}
                  </div>
                </div>
              </div>
            </div>

            {/* Form Content */}
            <div className="email-form-content">
              <form onSubmit={handleSendEmail}>
                {/* To Field */}
                <div className="email-form-group">
                  <div className="email-form-row">
                    <label className="email-form-label">
                      Email Address <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      value={emailForm.to}
                      onChange={(e) =>
                        setEmailForm({
                          ...emailForm,
                          to: e.target.value,
                        })
                      }
                      placeholder="professional@example.com"
                      required
                      disabled={sendingEmail}
                      className="email-form-input"
                      autoFocus
                    />
                  </div>
                  <p
                    style={{
                      fontSize: "0.85rem",
                      color: "#666",
                      marginTop: "0.5rem",
                      marginBottom: 0,
                    }}
                  >
                    We'll send them a personalized invitation with all the
                    rental details and a signup link.
                  </p>
                </div>

                {/* Action Buttons */}
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "flex-end",
                    paddingTop: "16px",
                    borderTop: "1px solid #e5e7eb",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailModal(false);
                      setSelectedOffer(null);
                      setEmailForm({ to: "", subject: "", message: "" });
                    }}
                    disabled={sendingEmail}
                    style={{
                      padding: "12px 24px",
                      background: "white",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#6b7280",
                      cursor: sendingEmail ? "not-allowed" : "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (!sendingEmail) {
                        e.target.style.borderColor = "#9ca3af";
                        e.target.style.color = "#374151";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!sendingEmail) {
                        e.target.style.borderColor = "#e5e7eb";
                        e.target.style.color = "#6b7280";
                      }
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={sendingEmail}
                    style={{
                      padding: "12px 32px",
                      background: sendingEmail
                        ? "#9ca3af"
                        : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "white",
                      cursor: sendingEmail ? "not-allowed" : "pointer",
                      transition: "all 0.2s",
                      boxShadow: sendingEmail
                        ? "none"
                        : "0 4px 12px rgba(102, 126, 234, 0.4)",
                    }}
                    onMouseEnter={(e) => {
                      if (!sendingEmail) {
                        e.target.style.transform = "translateY(-2px)";
                        e.target.style.boxShadow =
                          "0 6px 16px rgba(102, 126, 234, 0.5)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!sendingEmail) {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow =
                          "0 4px 12px rgba(102, 126, 234, 0.4)";
                      }
                    }}
                  >
                    {sendingEmail ? "Sending..." : "📧 Send Email"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </SidebarLayout>
  );
};

export default BoothRentalManagement;
